import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MagnifyingGlass, Trash, ShoppingCart, Truck, TelegramLogo, Pencil, Tag, MinusCircle, Plus, Hourglass, X, Megaphone } from '@phosphor-icons/react';
import { ExpenseDialog } from '@/components/pos/ExpenseDialog';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';
import { useAuth } from '@/contexts/AuthContext';
import { ProductEditDialog } from '@/components/common/ProductEditDialog';
import AddressPicker from '@/components/common/AddressPicker';
import { useSalesConfirmModal } from '@/components/common/SalesConfirmModal';
import { StaleCutWarning } from '@/components/common/StaleCutWarning';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_SHIPPING_COST = 90;

export const POS = () => {
  const {
    products, categories, sellers, clients, packages,
    refreshProducts, refreshSellers, refreshClients, refreshCategories, refreshPackages
  } = useDataCache();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { modal: salesConfirmModal, triggerCheck: triggerSalesConfirm } = useSalesConfirmModal();
  const [broadcasting, setBroadcasting] = useState(false);
  // Clave de localStorage para persistencia del carrito
  const CART_STORAGE_KEY = `pos_cart_state_v1_${user?.id || 'anon'}`;
  // Lazy initializer: evita leer storage en cada render
  const _loadInitial = () => {
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Expirar tras 24h sin actividad
      if (!parsed?.savedAt || (Date.now() - parsed.savedAt) > 24 * 60 * 60 * 1000) return null;
      return parsed;
    } catch { return null; }
  };
  const [expenseDialog, setExpenseDialog] = useState({ open: false, category: 'gasolina', amount: '', description: '', saving: false });
  const [priceChangeDialog, setPriceChangeDialog] = useState({ open: false, index: null, newPrice: '', reason: '' });
  const [pendingApprovals, setPendingApprovals] = useState({}); // index -> { request_id, status }
  const [cart, setCart] = useState(() => _loadInitial()?.cart || []);
  const [lowCostWait, setLowCostWait] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(() => _loadInitial()?.selectedSeller || '');
  const [sellerLockedByQr, setSellerLockedByQr] = useState(false);  // En modo venta, una vez escaneado un QR, no se puede cambiar
  const [scannedSellerName, setScannedSellerName] = useState(null); // Nombre mostrado tras escanear
  const [selectedClient, setSelectedClient] = useState(() => _loadInitial()?.selectedClient || 'none');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [currentPriceLevel, setCurrentPriceLevel] = useState(() => _loadInitial()?.currentPriceLevel || 'precio_1');
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [paymentSplit, setPaymentSplit] = useState({
    efectivo: 0,
    tarjeta: 0,
    transferencia: 0
  });
  const [showPaymentSplit, setShowPaymentSplit] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [variantModalProduct, setVariantModalProduct] = useState(null);
  const [showPackageDetail, setShowPackageDetail] = useState(null); // pkg object or null
  const [lightboxImage, setLightboxImage] = useState(null); // {url, title} or null
  const [shipmentPin, setShipmentPin] = useState(null); // {address, lat, lng, colonia} para el envío en curso
  const [editDialog, setEditDialog] = useState({ open: false, product: null, parent: null });
  const [clearSellerDialog, setClearSellerDialog] = useState({ open: false, action: 'cleared', reason: '', saving: false });
  // Hidden admin shortcut: Ctrl+Shift+V → assign seller manually after admin password
  const [adminAssignSeller, setAdminAssignSeller] = useState({ open: false, step: 'password', password: '', sellerId: '', saving: false });
  const [apartadoDialog, setApartadoDialog] = useState({
    open: false, client_name: '', client_phone: '', due_date: '', initial_payment: '',
    initial_payment_method: 'efectivo', deliver_now: false, notes: '', submitting: false,
  });
  const [cartMode, setCartMode] = useState(() => _loadInitial()?.cartMode || 'sale'); // 'sale' | 'shipment'
  const [nextFolio, setNextFolio] = useState(null);
  // 🎉 Promociones: overrides de precio calculados server-side + avisos de regalo disponible
  const [promoOverrides, setPromoOverrides] = useState({}); // { product_id: promoPrice }
  const [promoApplied, setPromoApplied] = useState([]);       // [{promo_id, type, product_id, qty}]
  const [promoHints, setPromoHints] = useState([]);           // [{gift_product_id, gift_product_id_name, trigger_product_id_name}]
  // Colonia quote helpers (POS modo envío)
  const [coloniaQuote, setColoniaQuote] = useState({ loading: false, match: null, suggestions: [], lastQuery: '' });
  const [shipmentForm, setShipmentForm] = useState(() => _loadInitial()?.shipmentForm || {
    client_phone: '',
    client_name: '',
    shipping_address: '',
    shipping_cost: '',
    notes: ''
  });
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [allowManualSeller, setAllowManualSeller] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [pendingListDialog, setPendingListDialog] = useState({ open: false, loading: false, items: [] });
  const [promoNotifyDialog, setPromoNotifyDialog] = useState({ open: false, selectedIds: new Set(), sending: false, promos: [], loadingPromos: false });
  // Ranking de ventas (product_id -> qty vendida en los últimos 60 días).
  // Se usa para ordenar los resultados de búsqueda: lo más vendido aparece primero.
  const [salesRanking, setSalesRanking] = useState({});

  const refetchAll = () => {
    refreshProducts();
    refreshSellers();
    refreshClients();
    refreshCategories();
    refreshPackages();
  };

  const handleBroadcastDailySummary = async () => {
    if (!window.confirm('¿Enviar el resumen del día por Telegram a TODOS los vendedores vinculados? Cada uno recibirá su ranking personalizado.')) return;
    setBroadcasting(true);
    try {
      const { data } = await axios.post(`${API}/telegram/broadcast-daily-summary`, {}, { withCredentials: true });
      if (data.total_linked === 0) {
        toast.warning('Ningún vendedor tiene Telegram vinculado todavía.');
      } else if (data.sent === 0) {
        toast.error('No se pudo enviar a ningún vendedor. Revisa los logs.');
      } else {
        const failedNote = data.failed > 0 ? ` · ${data.failed} fallaron` : '';
        toast.success(`Resumen enviado a ${data.sent} de ${data.total_linked} vendedor(es)${failedNote}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el resumen');
    } finally {
      setBroadcasting(false);
    }
  };

  const handleClientChange = (clientId) => {
    setSelectedClient(clientId);
    if (clientId === 'none') {
      setCurrentPriceLevel('precio_1');
    } else {
      const client = clients.find(c => c.id === clientId);
      if (client && client.precio_nivel) {
        setCurrentPriceLevel(client.precio_nivel);
      } else {
        setCurrentPriceLevel('precio_1');
      }
    }
  };

  // Si la marca seleccionada ya no existe dentro de la categoría actual, reset a "todas"
  useEffect(() => {
    if (selectedBrand !== 'all' && !availableBrands.includes(selectedBrand)) {
      setSelectedBrand('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, products]);

  // En modo VENTA, si no hay vendedor seleccionado todavía, default al vendedor "Local"
  useEffect(() => {
    if (cartMode !== 'sale') return;
    if (selectedSeller) return;
    const local = (sellers || []).find(s => s.is_local) || (sellers || []).find(s => s.name === 'Local');
    if (local) setSelectedSeller(local.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellers, cartMode]);

  // Persistir carrito y selección en localStorage. Sobrevive al cambiar de pestaña
  // o al recargar. Se borra explícitamente cuando se completa una venta/envío.
  useEffect(() => {
    try {
      if (cart.length === 0) {
        // Carrito vacío: limpiar storage para no arrastrar selección de venta anterior.
        localStorage.removeItem(CART_STORAGE_KEY);
        return;
      }
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
        cart, selectedSeller, selectedClient, currentPriceLevel, cartMode,
        shipmentForm, savedAt: Date.now(),
      }));
    } catch { /* quota / disabled storage */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, selectedSeller, selectedClient, currentPriceLevel, cartMode, shipmentForm]);

  // 🎉 Preview de promociones: se recalcula al cambiar carrito o vendedor.
  useEffect(() => {
    // Reset si carrito vacío
    if (!cart || cart.length === 0) {
      setPromoOverrides({});
      setPromoApplied([]);
      setPromoHints([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const items = cart.filter(it => it.product_id || it.package_id).map(it => ({
          product_id: it.product_id || null, package_id: it.package_id || null,
          quantity: it.quantity, unit_price: it.unit_price, price_type: it.price_type,
          manual_price: !!it.manual_price,
        }));
        if (items.length === 0) {
          setPromoOverrides({}); setPromoApplied([]); setPromoHints([]);
          return;
        }
        const { data } = await axios.post(`${API}/promotions/preview`,
          { items, seller_id: selectedSeller || null, is_shipment: cartMode === 'shipment' },
          { withCredentials: true });
        const overrides = {};
        (data.items || []).forEach(it => {
          if (it.promo_applied) overrides[it.product_id || it.package_id] = it.unit_price;
        });
        setPromoOverrides(overrides);
        setPromoApplied(data.applied || []);

        // Sugerencias: promos "gift" cuyo trigger está en el carrito pero cuyo gift no está
        const pidsInCart = new Set(cart.map(it => it.product_id).filter(Boolean));
        const hintsRes = await axios.get(
          `${API}/promotions/active-for-cart?product_ids=${Array.from(pidsInCart).join(',')}`,
          { withCredentials: true });
        const hints = (hintsRes.data || []).filter(p =>
          p.promo_type === 'gift'
          && p.has_promo
          && pidsInCart.has(p.id)                       // trigger en carrito
          && p.promo_gift_product_id
          && !pidsInCart.has(p.promo_gift_product_id)   // regalo aún no en carrito
        );
        setPromoHints(hints);
      } catch {
        // best-effort
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [cart, selectedSeller, cartMode]);

  // Cargar config: permitir vender sin existencias (admin toggle)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/config/allow-negative-stock`, { withCredentials: true });
        setAllowNegativeStock(!!data.enabled);
      } catch (e) {
        // ignore
      }
      try {
        const { data } = await axios.get(`${API}/config/allow-manual-seller`, { withCredentials: true });
        setAllowManualSeller(!!data.enabled);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  // Cargar ranking de ventas para ordenar resultados de búsqueda
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products/ranking/by-sales?days=60`, { withCredentials: true });
        setSalesRanking(data || {});
      } catch { /* ignore */ }
    })();
  }, []);

  // Polling del badge de aprobaciones pendientes del usuario actual.
  // Cada 4s consultamos /approvals/pending/me/count para reflejar cuando el
  // admin aprueba o rechaza, o cuando el clerk envía nuevas solicitudes.
  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const { data } = await axios.get(`${API}/approvals/pending/me/count`, { withCredentials: true });
        if (alive) setPendingApprovalsCount(Number(data?.count || 0));
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 4000);
    return () => { alive = false; clearInterval(interval); };
  }, []);

  // ============== QR SCANNER LISTENER ==============
  // Escucha global de teclado: cuando un lector QR/USB envía una cadena rápida + Enter,
  // intentamos validarla contra /api/sellers/by-qr/{code}.
  useEffect(() => {
    let buffer = '';
    let lastKeyTime = 0;
    const QR_GAP_MS = 50;       // entre tecla y tecla, escaner es muy rápido
    const QR_MIN_LEN = 10;      // los códigos tienen 15 chars; tolerar tipos un poco más cortos

    const handler = async (e) => {
      const now = Date.now();
      // Si se demora demasiado entre teclas, asumir que NO es escaneo y empezar de cero
      if (now - lastKeyTime > 200) {
        buffer = '';
      }
      lastKeyTime = now;

      if (e.key === 'Enter') {
        const code = buffer.trim();
        buffer = '';
        if (code.length < QR_MIN_LEN) return; // texto humano, ignorar
        // Sólo procesar si parece QR (alfanumérico)
        if (!/^[A-Za-z0-9]+$/.test(code)) return;
        // No procesar si el usuario está escribiendo activamente en un input/textarea
        const tag = (e.target && e.target.tagName) || '';
        const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable);
        if (isTyping && (now - lastKeyTime > QR_GAP_MS * 2)) return;
        // Buscar vendedor
        try {
          const { data } = await axios.get(`${API}/sellers/by-qr/${encodeURIComponent(code)}`, { withCredentials: true });
          if (cartMode === 'sale' && sellerLockedByQr) {
            toast.warning('Ya hay un vendedor escaneado para esta venta. Cancela o completa la venta para cambiarlo.');
            return;
          }
          setSelectedSeller(data.id);
          setScannedSellerName(data.name);
          if (cartMode === 'sale') setSellerLockedByQr(true);
          toast.success(`Vendedor: ${data.name}`);
          // Notificar al propietario (best-effort, fail silent)
          axios.post(`${API}/sellers/qr-scanned`, { seller_id: data.id }, { withCredentials: true })
            .catch(() => {});
          e.preventDefault();
        } catch (err) {
          // Código no reconocido: silencioso (no avisar a cada error random)
        }
        return;
      }

      // Caracter normal: acumular si parece QR
      if (e.key && e.key.length === 1 && /[A-Za-z0-9]/.test(e.key)) {
        buffer += e.key;
        if (buffer.length > 30) buffer = buffer.slice(-30);
      }
      // Teclas que algunos lectores intercalan pero NO deben reiniciar el buffer
      // (Clear, Shift, CapsLock, NumLock, AltGraph, Control, Alt, Meta, Dead, Process, Unidentified)
      else if (['Clear','Shift','CapsLock','NumLock','AltGraph','Control','Alt','Meta','Dead','Process','Unidentified'].includes(e.key)) {
        // ignorar — no rompen el escaneo
      }
      else {
        // tecla rara durante un posible scan -> reset
        buffer = '';
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [cartMode, sellerLockedByQr]);

  // ---------- HIDDEN ADMIN SHORTCUT: Ctrl+Shift+V → manual seller assign ----------
  useEffect(() => {
    const onKey = (e) => {
      // Ctrl + Shift + V (mayúscula o minúscula). No interferir con la escritura normal.
      if (e.ctrlKey && e.shiftKey && (e.key === 'V' || e.key === 'v')) {
        e.preventDefault();
        e.stopPropagation();
        setAdminAssignSeller({ open: true, step: 'password', password: '', sellerId: '', saving: false });
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  // ---------- Debounced colonia → quote ----------
  useEffect(() => {
    if (cartMode !== 'shipment') return;
    const q = (shipmentForm.shipping_address || '').trim();
    if (!q) {
      setColoniaQuote({ loading: false, match: null, suggestions: [], lastQuery: '' });
      return;
    }
    if (q === coloniaQuote.lastQuery) return;
    const handle = setTimeout(async () => {
      setColoniaQuote((s) => ({ ...s, loading: true }));
      try {
        const { data } = await axios.get(`${API}/shipping-zones/quote?q=${encodeURIComponent(q)}`, { withCredentials: true });
        setColoniaQuote({ loading: false, match: data.match, suggestions: data.suggestions || [], lastQuery: q });
        // NOTE: NO auto-llenamos `shipping_cost`. El sistema solo muestra la sugerencia
        // (✓ Colonia · $XXX). El cajero captura el monto manualmente para evitar errores
        // cuando la dirección no coincide exactamente con la tarifa esperada.
      } catch (e) {
        setColoniaQuote({ loading: false, match: null, suggestions: [], lastQuery: q });
      }
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentForm.shipping_address, cartMode]);

  const applyColoniaSuggestion = (s) => {
    // Aplica solo la colonia. NO auto-llenamos el costo: el cajero lo captura manualmente.
    setShipmentForm((f) => ({
      ...f,
      shipping_address: s.colonia,
    }));
    setColoniaQuote({ loading: false, match: s, suggestions: [], lastQuery: s.colonia });
  };

  const filteredProducts = products.filter((p) => {
    // Excluir productos marcados como NO visibles en POS
    if (p.visible_in_pos === false) return false;

    const term = searchTerm.trim().toLowerCase();
    const isVariant = !!p.parent_product_id;

    // Una variante solo aparece cuando coincide directamente con la búsqueda.
    // Se renderiza como su propia card "agregar a un click" sin abrir modal del padre.
    if (isVariant) {
      if (!term) return false;
      // Si el PADRE está oculto del POS, la variante también debe ocultarse
      const parent = products.find((x) => x.id === p.parent_product_id);
      if (parent && parent.visible_in_pos === false) return false;
      const matchesVariant =
        (p.variant_name || '').toLowerCase().includes(term) ||
        (p.name || '').toLowerCase().includes(term) ||
        (p.sku || '').toLowerCase().includes(term);
      if (!matchesVariant) return false;
      // Filtro por marca/categoría — heredan del padre si no tienen propio
      const categoryId = p.category_id || parent?.category_id;
      const brand = p.brand || parent?.brand || '';
      if (selectedCategory !== 'all') {
        const subIds = categories.filter((c) => c.parent_id === selectedCategory).map((c) => c.id);
        const allowedIds = [selectedCategory, ...subIds];
        if (!allowedIds.includes(categoryId)) return false;
      }
      if (selectedBrand !== 'all' && brand !== selectedBrand) return false;
      return true;
    }

    // Padre: filtros normales + búsqueda
    if (selectedCategory !== 'all') {
      const subIds = categories.filter((c) => c.parent_id === selectedCategory).map((c) => c.id);
      const allowedIds = [selectedCategory, ...subIds];
      if (!allowedIds.includes(p.category_id)) return false;
    }
    if (selectedBrand !== 'all' && (p.brand || '') !== selectedBrand) return false;
    if (!term) return true;
    // El padre matchea por sus propios campos. Si NO matchea pero alguna variante
    // sí matchea con la búsqueda, ocultamos el padre — la variante aparecerá
    // como card propia.
    return (
      (p.name || '').toLowerCase().includes(term) ||
      (p.sku || '').toLowerCase().includes(term) ||
      (p.brand || '').toLowerCase().includes(term)
    );
  });

  const filteredPackages = (packages || []).filter((pkg) => {
    if (selectedCategory !== 'all') {
      const subIds = categories.filter((c) => c.parent_id === selectedCategory).map((c) => c.id);
      const allowedIds = [selectedCategory, ...subIds];
      if (!allowedIds.includes(pkg.category_id)) return false;
    }
    if (selectedBrand !== 'all') {
      if ((pkg.brand || '') !== selectedBrand) return false;
    }
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      (pkg.name || '').toLowerCase().includes(term) ||
      (pkg.sku || '').toLowerCase().includes(term) ||
      (pkg.brand || '').toLowerCase().includes(term)
    );
  });

  // Cuando hay búsqueda activa, ordenar por más vendido (ranking) primero.
  // Para variantes, se suma su propio qty + el del padre (si lo hay) para
  // que las variantes populares también suban en el orden.
  if (searchTerm.trim()) {
    const rank = (p) => {
      const own = Number(salesRanking[p.id]) || 0;
      const parent = p.parent_product_id ? (Number(salesRanking[p.parent_product_id]) || 0) : 0;
      return own + parent;
    };
    filteredProducts.sort((a, b) => rank(b) - rank(a));
    filteredPackages.sort((a, b) => (Number(salesRanking[b.id]) || 0) - (Number(salesRanking[a.id]) || 0));
  }

  // Marcas disponibles: SOLO de productos visibles dentro de la categoría seleccionada
  const availableBrands = Array.from(
    new Set(
      products
        .filter(p => !p.parent_product_id && p.visible_in_pos !== false && p.brand)
        .filter(p => {
          if (selectedCategory === 'all') return true;
          const subIds = categories.filter((c) => c.parent_id === selectedCategory).map((c) => c.id);
          const allowedIds = [selectedCategory, ...subIds];
          return allowedIds.includes(p.category_id);
        })
        .map(p => p.brand)
    )
  ).sort();

  const parentCategories = categories.filter((c) => !c.parent_id);
  const getSubcategoriesOf = (parentId) => categories.filter((c) => c.parent_id === parentId);
  const isSubcategory = (catId) => {
    const cat = categories.find((c) => c.id === catId);
    return cat && cat.parent_id;
  };
  // Si se selecciona una subcategoría, su padre se considera "activo" para mostrar la barra de subs
  const activeParentId = selectedCategory === 'all'
    ? null
    : (isSubcategory(selectedCategory) ? categories.find((c) => c.id === selectedCategory)?.parent_id : selectedCategory);
  const activeSubcategories = activeParentId ? getSubcategoriesOf(activeParentId) : [];

  const getVariantsOf = (productId) => products.filter((p) => p.parent_product_id === productId && p.visible_in_pos !== false);

  const handleProductClick = (product) => {
    if (product.has_variants) {
      setVariantModalProduct(product);
    } else {
      addToCart(product);
    }
  };

  // ---------- PROMO HELPERS ----------
  // 🚚 Productos del carrito con promo "envío gratis" vigente
  const freeShippingNames = cart
    .map((it) => products.find((p) => p.id === it.product_id))
    .filter((p) => p && p.has_promo && p.promo_type === 'free_shipping'
      && (!p.promo_end_date || new Date() <= new Date(p.promo_end_date + 'T23:59:59'))
      && (!p.promo_seller_restricted || (p.promo_allowed_seller_ids || []).includes(selectedSeller)))
    .map((p) => p.name);

  // 🔗 Combo: avisar si el producto con descuento está con 2+ piezas (uno por compra)
  const cartKeys = new Set(cart.map(it => it.product_id || it.package_id).filter(Boolean));
  const comboWarnings = cart
    .filter(it => it.quantity > 1)
    .map(it => (it.package_id ? packages : products).find(p => p.id === (it.product_id || it.package_id)))
    .filter(p => p && p.has_promo && p.promo_type === 'combo' && cartKeys.has(p.promo_combo_product_id))
    .map(p => p.name);

  // 💡 Sugerir completar el combo: el "otro" producto está en el carrito y falta el que lleva descuento
  const comboSuggestions = [...products, ...packages.map(pk => ({ ...pk, is_package: true }))]
    .filter(p => p.has_promo && p.promo_type === 'combo' && Number(p.promo_combo_discount) > 0
      && cartKeys.has(p.promo_combo_product_id) && !cartKeys.has(p.id)
      && (!p.promo_end_date || new Date() <= new Date(p.promo_end_date + 'T23:59:59')));  const isPromoActive = (product) => {
    if (!product?.has_promo) return false;
    const isGift = ['gift', 'free_shipping', 'combo', 'percent_discount'].includes(product.promo_type);
    // gift / envío gratis no tienen promo_price; aún así cuentan como promo activa (badge).
    if (!isGift && !product.promo_price) return false;
    if (product.promo_end_date) {
      const endDate = new Date(product.promo_end_date + 'T23:59:59');
      if (new Date() > endDate) return false;
    }
    // Alcance: si es solo local y estamos en envío → no aplica.
    // Si es solo envío y estamos en venta local → no aplica.
    if (product.promo_local_only && cartMode === 'shipment') return false;
    if (product.promo_shipment_only && cartMode === 'sale') return false;
    if (product.promo_seller_restricted && !(product.promo_allowed_seller_ids || []).includes(selectedSeller)) return false;
    // Las promociones SÓLO aplican cuando el nivel de precio activo es `precio_1`.
    // Para niveles mayoristas (mayoreo, medio_mayoreo, precio_2..5) se respeta el
    // precio del nivel: esos niveles ya llevan descuento, la promo no se sobrepone.
    if (currentPriceLevel !== 'precio_1') return false;
    return true;
  };

  // ¿Algún producto del padre (o el padre mismo si no tiene variantes) tiene promo activa?
  const parentHasActivePromo = (parent) => {
    if (parent.has_variants) {
      return getVariantsOf(parent.id).some((v) => isPromoActive(v));
    }
    return isPromoActive(parent);
  };

  // Etiqueta corta para el badge de promo en la tarjeta
  const promoBadgeLabel = (product) => {
    if (!isPromoActive(product)) return null;
    if (product.promo_type === 'gift') return '🎁 REGALO';
    if (product.promo_type === 'free_shipping') return '🚚 ENVÍO GRATIS';
    if (product.promo_type === 'combo') return `🔗 -$${Number(product.promo_combo_discount || 0).toFixed(0)} COMBO`;
    if (product.promo_type === 'min_qty') {
      const n = product.promo_min_qty || 0;
      return n > 0 ? `OFERTA · ≥${n}` : 'OFERTA';
    }
    if (product.promo_type === 'percent_discount' && product.promo_discount_percent) {
      return `-${Math.round(Number(product.promo_discount_percent))}%`;
    }
    if (product.promo_type === 'limited_qty') return 'OFERTA · LIMITADA';
    return 'OFERTA';
  };

  // Si el producto no tiene precio definido para el nivel activo (precio_2..5,
  // mayoreo, medio_mayoreo) o vale 0, se mantiene el `precio_1` como fallback.
  const getPriceAtLevel = (item, level) => {
    if (!item) return 0;
    const v = Number(item[level]);
    if (v > 0) return v;
    return Number(item.precio_1) || 0;
  };

  const computeEffectivePrice = (product, qty) => {
    const baseLevel = getPriceAtLevel(product, currentPriceLevel);
    // isPromoActive ya considera el nivel actual (sólo aplica en precio_1)
    if (!isPromoActive(product)) return { price: baseLevel, isPromo: false };
    // Gift: el trigger NO tiene descuento, sólo se marca la promo en la tarjeta.
    if (['gift', 'free_shipping', 'combo'].includes(product.promo_type)) return { price: baseLevel, isPromo: false };
    if (product.promo_type === 'flat') {
      return { price: product.promo_price, isPromo: true };
    }
    if (product.promo_type === 'min_qty') {
      const minQty = product.promo_min_qty || 0;
      if (qty >= minQty) return { price: product.promo_price, isPromo: true };
    }
    return { price: baseLevel, isPromo: false };
  };

  const addToCart = (product) => {
    const commission = product.default_commission || 0;
    const existingItem = cart.find((item) => item.product_id === product.id);

    if (existingItem) {
      // Actualizar cantidad y reaplicar lógica de promo si corresponde
      const newQty = existingItem.quantity + 1;
      const { price: newPrice, isPromo } = computeEffectivePrice(product, newQty);
      setCart(
        cart.map((item) =>
          item.product_id === product.id
            ? {
                ...item,
                quantity: newQty,
                // Solo aplicar promo automática si NO está bajo precio manual
                unit_price: item.manual_price ? item.unit_price : newPrice,
                is_promo: item.manual_price ? false : isPromo
              }
            : item
        )
      );
    } else {
      const { price, isPromo } = computeEffectivePrice(product, 1);
      setCart([
        ...cart,
        {
          product_id: product.id,
          product_name: product.variant_name ? `${product.name} (${product.variant_name})` : product.name,
          quantity: 1,
          price_type: currentPriceLevel,
          unit_price: price,
          original_price: getPriceAtLevel(product, currentPriceLevel),
          commission: commission,
          image_url: product.image_url || null,
          is_promo: isPromo,
          promo_min_qty: product.promo_min_qty || null,
          manual_price: false
        }
      ]);
    }
    toast.success(`${product.name} agregado al carrito`);
  };

  const addPackageToCart = (pkg) => {
    const priceLevelKey = currentPriceLevel; // precio_1..5
    let unitPrice = getPriceAtLevel(pkg, priceLevelKey);
    let isPromo = false;
    // Aplicar promoción usando la misma lógica que productos (isPromoActive/computeEffectivePrice)
    if (isPromoActive(pkg)) {
      if (pkg.promo_type === 'flat') {
        unitPrice = Number(pkg.promo_price);
        isPromo = true;
      } else if (pkg.promo_type === 'min_qty') {
        // Al agregar 1 pieza, la promo min_qty aún no aplica; se re-evalúa al subir cantidad
        // dentro de updateQuantity (misma lógica que productos).
      } else if (!pkg.promo_type) {
        // Sin promo_type explícito, asumir flat como legacy
        const promoP = Number(pkg.promo_price);
        if (promoP > 0 && promoP < unitPrice) {
          unitPrice = promoP;
          isPromo = true;
        }
      }
    }
    const commission = parseFloat(pkg.default_commission || 0);
    const existingIdx = cart.findIndex(it => it.package_id === pkg.id);
    if (existingIdx !== -1) {
      const newCart = [...cart];
      newCart[existingIdx] = { ...newCart[existingIdx], quantity: newCart[existingIdx].quantity + 1 };
      setCart(newCart);
    } else {
      setCart([...cart, {
        package_id: pkg.id,
        product_id: null,
        product_name: `📦 ${pkg.name}${isPromo ? ' 🏷️' : ''}`,
        quantity: 1,
        price_type: isPromo ? 'promo' : priceLevelKey,
        unit_price: unitPrice,
        original_price: unitPrice,
        commission: commission,
        image_url: pkg.image_url || null,
        is_promo: isPromo,
        is_package: true,
        manual_price: false
      }]);
    }
    toast.success(`Paquete ${pkg.name}${isPromo ? ' (promo)' : ''} agregado`);
  };

  const updateQuantity = (index, quantity) => {
    if (quantity <= 0) {
      removeFromCart(index);
      return;
    }
    const newCart = [...cart];
    newCart[index].quantity = quantity;
    // Reaplicar promo automática (solo si NO está con precio manual)
    if (!newCart[index].manual_price) {
      if (newCart[index].package_id) {
        const pkg = packages.find((p) => p.id === newCart[index].package_id);
        if (pkg) {
          const { price, isPromo } = computeEffectivePrice(pkg, quantity);
          newCart[index].unit_price = price;
          newCart[index].is_promo = isPromo;
        }
      } else {
        const product = products.find((p) => p.id === newCart[index].product_id);
        if (product) {
          const { price, isPromo } = computeEffectivePrice(product, quantity);
          newCart[index].unit_price = price;
          newCart[index].is_promo = isPromo;
        }
      }
    }
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
    setPendingApprovals((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  // Re-precia el carrito cuando cambia el nivel de precio (p.ej. al cambiar de cliente).
  // - Ítems con precio manual (`manual_price=true`) se respetan tal cual.
  // - Productos: reusa computeEffectivePrice (incluye promo flat o por mínimo de piezas).
  // - Paquetes: toma pkg[currentPriceLevel] o pkg.precio_1.
  useEffect(() => {
    if (cart.length === 0) return;
    let changed = false;
    const next = cart.map((item) => {
      if (item.manual_price) return item;
      if (item.package_id) {
        const pkg = packages.find((p) => p.id === item.package_id);
        if (!pkg) return item;
        const base = getPriceAtLevel(pkg, currentPriceLevel);
        let { price: newPrice, isPromo } = computeEffectivePrice(pkg, item.quantity || 1);
        if (!pkg.promo_type && isPromoActive(pkg) && Number(pkg.promo_price) > 0 && Number(pkg.promo_price) < base) {
          newPrice = Number(pkg.promo_price); isPromo = true;
        }
        if (newPrice !== item.unit_price || base !== item.original_price || currentPriceLevel !== item.price_type) {
          changed = true;
          return { ...item, unit_price: newPrice, original_price: base, is_promo: isPromo, price_type: currentPriceLevel };
        }
        return item;
      }
      const product = products.find((p) => p.id === item.product_id);
      if (!product) return item;
      const { price, isPromo } = computeEffectivePrice(product, item.quantity || 1);
      const newOriginal = getPriceAtLevel(product, currentPriceLevel);
      if (price !== item.unit_price || newOriginal !== item.original_price || currentPriceLevel !== item.price_type) {
        changed = true;
        return {
          ...item,
          unit_price: price,
          original_price: newOriginal,
          is_promo: isPromo,
          price_type: currentPriceLevel
        };
      }
      return item;
    });
    if (changed) {
      setCart(next);
      toast.info(`Carrito actualizado (${cartMode === 'shipment' ? 'envío' : 'venta local'} · ${currentPriceLevel.replace('_', ' ')})`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPriceLevel, cartMode, selectedSeller]);

  // ---------- PRICE CHANGE FLOW ----------
  const openPriceChangeDialog = (index) => {
    const item = cart[index];
    setPriceChangeDialog({
      open: true,
      index,
      newPrice: item.unit_price.toFixed(2),
      reason: ''
    });
  };

  const submitPriceChange = async () => {
    const { index, newPrice, reason } = priceChangeDialog;
    if (index === null) return;
    const newPriceNum = parseFloat(newPrice);
    if (isNaN(newPriceNum) || newPriceNum < 0) {
      toast.error('Precio inválido');
      return;
    }
    if (!isAdmin && !reason.trim()) {
      toast.error('Debes indicar el motivo');
      return;
    }
    const item = cart[index];
    // Los paquetes usan `package_id` en el carrito (no `product_id`).
    // El backend acepta ambos IDs en /approvals/price-change.
    const targetId = item.product_id || item.package_id;
    if (!targetId) {
      toast.error('Este ítem no soporta cambio de precio con aprobación');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/approvals/price-change`,
        {
          product_id: targetId,
          cart_line_id: `cart-${index}`,
          original_price: item.unit_price,
          requested_price: newPriceNum,
          quantity: item.quantity,
          reason: reason || 'Admin manual'
        },
        { withCredentials: true }
      );
      setPriceChangeDialog({ open: false, index: null, newPrice: '', reason: '' });

      if (data.status === 'approved') {
        // Auto-aprobado (admin)
        const newCart = [...cart];
        newCart[index].unit_price = newPriceNum;
        newCart[index].manual_price = true;
        newCart[index].is_promo = false;
        setCart(newCart);
        toast.success('Precio actualizado');
      } else {
        // Pendiente: marcar como esperando aprobación
        setPendingApprovals((prev) => ({
          ...prev,
          [index]: { request_id: data.id, requested_price: newPriceNum }
        }));
        toast.info('Esperando aprobación del admin…');
      }
    } catch (error) {
      const raw = error.response?.data?.detail;
      const msg = Array.isArray(raw)
        ? raw.map(x => `${(x.loc||[]).slice(-1)}: ${x.msg}`).join(' · ')
        : (typeof raw === 'string' ? raw : (raw?.message || 'Error al solicitar cambio de precio'));
      toast.error(msg);
    }
  };

  // Polling de autorización de envío menor al mínimo
  const submitShipmentRef = useRef(null);
  useEffect(() => { submitShipmentRef.current = submitShipment; });
  useEffect(() => {
    if (!lowCostWait) return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/approvals/${lowCostWait.id}`, { withCredentials: true });
        if (data.status === 'approved') {
          setLowCostWait(null);
          toast.success('✅ Costo de envío autorizado, registrando envío…');
          submitShipmentRef.current?.(lowCostWait.id);
        } else if (['rejected', 'expired'].includes(data.status)) {
          setLowCostWait(null);
          toast.error('❌ Costo de envío no autorizado. El envío no se registró.');
        }
      } catch { /* ignore */ }
    }, 2500);
    return () => clearInterval(t);
  }, [lowCostWait]);

  // Polling de aprobaciones pendientes
  useEffect(() => {
    const ids = Object.entries(pendingApprovals);
    if (ids.length === 0) return;
    const interval = setInterval(async () => {
      for (const [idx, info] of ids) {
        try {
          const { data } = await axios.get(`${API}/approvals/${info.request_id}`, { withCredentials: true });
          if (data.status === 'approved') {
            // aplicar
            setCart((prev) => {
              const next = [...prev];
              const i = parseInt(idx);
              if (next[i]) {
                next[i].unit_price = info.requested_price;
                next[i].manual_price = true;
                next[i].is_promo = false;
              }
              return next;
            });
            setPendingApprovals((prev) => {
              const next = { ...prev };
              delete next[idx];
              return next;
            });
            toast.success(`✅ Cambio de precio aprobado (línea ${parseInt(idx) + 1})`);
          } else if (data.status === 'rejected') {
            setPendingApprovals((prev) => {
              const next = { ...prev };
              delete next[idx];
              return next;
            });
            toast.error(`❌ Cambio de precio rechazado (línea ${parseInt(idx) + 1})`);
          }
        } catch (e) { /* ignore */ }
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [pendingApprovals]);

  const calculateTotal = () => {
    return cart.reduce((sum, item) => {
      // Si el cajero obtuvo un cambio de precio aprobado para esta línea
      // (manual_price=true), ese precio GANA sobre cualquier promo. Si no,
      // se aplica el override de promo calculado por el backend.
      const price = item.manual_price
        ? item.unit_price
        : (promoOverrides[item.product_id || item.package_id] !== undefined ? promoOverrides[item.product_id || item.package_id] : item.unit_price);
      return sum + price * item.quantity;
    }, 0);
  };

  const calculateTotalCommission = () => {
    return cart.reduce((sum, item) => sum + (item.commission || 0) * item.quantity, 0);
  };

  const _doSwitchToShipmentMode = () => {
    // Pre-llenar el nombre del cliente si hay uno seleccionado
    const client = selectedClient !== 'none' ? clients.find(c => c.id === selectedClient) : null;
    setShipmentForm({
      client_phone: client?.phone || '',
      client_name: client?.name || '',
      shipping_address: '',
      shipping_cost: '',
      notes: ''
    });
    setCartMode('shipment');
    // Pedir el folio que le tocará al próximo envío
    axios.get(`${API}/shipments/next-folio`, { withCredentials: true })
      .then(({ data }) => setNextFolio(data.folio))
      .catch(() => setNextFolio(null));
  };

  const switchToShipmentMode = () => {
    // Si el vendedor está bloqueado por QR y el cajero quiere pasar a envío,
    // pedir razón obligatoria (porque el cliente referido no se queda como venta)
    if (sellerLockedByQr) {
      setClearSellerDialog({ open: true, action: 'switched_to_shipment', reason: '', saving: false });
      return;
    }
    _doSwitchToShipmentMode();
  };

  const switchToSaleMode = () => {
    setCartMode('sale');
    // Al pasar de envío a venta, SIEMPRE reset a "Local" para evitar mal uso
    const local = (sellers || []).find(s => s.is_local) || (sellers || []).find(s => s.name === 'Local');
    setSelectedSeller(local ? local.id : '');
    setSellerLockedByQr(false);
    setScannedSellerName(null);
  };

  const submitApartado = async () => {
    const d = apartadoDialog;
    if (!d.client_name.trim()) { toast.error('Nombre obligatorio'); return; }
    if (!d.client_phone.trim()) { toast.error('Teléfono obligatorio'); return; }
    if (!d.due_date) { toast.error('Fecha de vencimiento obligatoria'); return; }
    const amount = parseFloat(d.initial_payment);
    if (!amount || amount <= 0) { toast.error('Monto del enganche inválido'); return; }
    setApartadoDialog(s => ({ ...s, submitting: true }));
    try {
      const items = cart.filter(i => i.product_id).map(i => ({
        product_id: i.product_id,
        quantity: i.quantity,
        price_type: i.price_type,
        unit_price: i.unit_price,
        commission: i.commission || 0,
      }));
      if (items.length === 0) { toast.error('El apartado no soporta paquetes; usa sólo productos'); setApartadoDialog(s => ({ ...s, submitting: false })); return; }
      const payload = {
        client_name: d.client_name.trim(),
        client_phone: d.client_phone.trim(),
        due_date: d.due_date,
        seller_id: selectedSeller,
        items,
        initial_payment: amount,
        initial_payment_method: d.initial_payment_method,
        deliver_now: d.deliver_now,
        notes: d.notes.trim() || null,
      };
      const { data } = await axios.post(`${API}/apartados`, payload, { withCredentials: true });
      toast.success(`📋 Apartado ${data.folio} · resta $${data.remaining_amount.toFixed(2)}`);
      setCart([]);
      setSelectedClient('none');
      setCurrentPriceLevel('precio_1');
      setApartadoDialog({
        open: false, client_name: '', client_phone: '', due_date: '', initial_payment: '',
        initial_payment_method: 'efectivo', deliver_now: false, notes: '', submitting: false,
      });
      refreshProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear apartado');
      setApartadoDialog(s => ({ ...s, submitting: false }));
    }
  };

  const submitShipment = async (lowCostApprovalId = null) => {
    if (!selectedSeller) {
      toast.error('Selecciona un vendedor');
      return;
    }
    // Bloquear cerrar envío si el vendedor es "Local" (in-store default)
    const seller = (sellers || []).find(s => s.id === selectedSeller);
    if (seller && (seller.is_local || seller.name === 'Local')) {
      toast.error('Los envíos no pueden atribuirse a "Local". Escanea el QR del vendedor o selecciona uno antes de cerrar.');
      return;
    }
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }
    if (!shipmentForm.client_name.trim() || !shipmentForm.client_phone.trim() ||
        !shipmentForm.shipping_address.trim() || !shipmentForm.shipping_cost) {
      toast.error('Todos los campos del envío son obligatorios');
      return;
    }
    // Pin de mapa obligatorio — no dejamos cerrar envíos sin ubicación GPS
    if (!shipmentPin?.lat || !shipmentPin?.lng) {
      toast.error('Coloca el pin de la dirección en el mapa antes de cerrar el envío.');
      return;
    }
    const shippingCost = parseFloat(shipmentForm.shipping_cost);
    if (isNaN(shippingCost) || shippingCost < 0) {
      toast.error('Costo de envío inválido');
      return;
    }
    let freeShippingOverride = false;
    if (freeShippingNames.length > 0 && shippingCost > 0) {
      if (!window.confirm(`🚚 Tienes un producto con ENVÍO GRATIS:\n${freeShippingNames.join(', ')}\n\n¿Estás seguro que quieres colocar costo de envío ($${shippingCost.toFixed(2)})?\nSe enviará la aprobación por Telegram. Mientras tanto el envío se registra con costo $0.`)) return;
      freeShippingOverride = true;
    }
    // 💵 Envío menor al mínimo (sin promo envío gratis) → autorización por Telegram antes de registrar
    if (freeShippingNames.length === 0 && shippingCost < MIN_SHIPPING_COST && !lowCostApprovalId) {
      if (!window.confirm(`El costo de envío ($${shippingCost.toFixed(2)}) es menor a $${MIN_SHIPPING_COST}.\n\nSe pedirá autorización por Telegram. El envío se registrará solo cuando la aprueben.`)) return;
      try {
        const { data } = await axios.post(`${API}/approvals/low-shipping-cost`, {
          shipping_cost: shippingCost,
          client_name: shipmentForm.client_name,
          items_total: cart.reduce((s, it) => s + (it.unit_price || 0) * (it.quantity || 0), 0),
        }, { withCredentials: true });
        if (data.status === 'approved') return submitShipment(data.id);
        setLowCostWait({ id: data.id, cost: shippingCost });
        toast.info('⏳ Esperando autorización del costo de envío por Telegram…');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'No se pudo pedir la autorización');
      }
      return;
    }

    const buildPayload = (pinVerified = false) => ({
      items: cart.map(it => ({
        product_id: it.product_id || null,
        product_name: it.product_name,
        quantity: it.quantity,
        price_type: it.price_type,
        unit_price: it.unit_price,
        commission: it.commission || 0,
        package_id: it.package_id || null,
        is_package: !!it.is_package
      })),
      seller_id: selectedSeller,
      client_id: selectedClient === 'none' ? null : selectedClient,
      client_name: shipmentForm.client_name.trim(),
      client_phone: shipmentForm.client_phone.trim(),
      shipping_address: shipmentForm.shipping_address.trim(),
      shipping_lat: shipmentPin?.lat || null,
      shipping_lng: shipmentPin?.lng || null,
      shipping_colonia: shipmentPin?.colonia || null,
      shipping_cost: shippingCost,
      notes: shipmentForm.notes.trim() || null,
      pin_verified: pinVerified,
      free_shipping_override: freeShippingOverride,
      low_cost_approval_id: lowCostApprovalId,
    });

    setLoading(true);
    const submit = async (pinVerified = false) => {
      const { data } = await axios.post(`${API}/shipments`, buildPayload(pinVerified), { withCredentials: true });
      return data;
    };
    try {
      let data;
      try {
        data = await submit(false);
      } catch (err) {
        const msg = err?.response?.data?.detail;
        // Si el backend rechazó por colonia mismatch, permitir override con confirmación
        if (typeof msg === 'string' && msg.includes('apunta a') && msg.includes('colonia')) {
          if (window.confirm(`${msg}\n\n¿Confirmar que el pin es el correcto y continuar de todas formas?`)) {
            data = await submit(true);
          } else {
            throw err;
          }
        } else {
          throw err;
        }
      }
      toast.success(freeShippingOverride
        ? `Envío ${data.folio} creado con envío $0. Costo enviado a aprobación por Telegram.`
        : `Envío ${data.folio} creado. Asignar entregador en Envíos.`);
      setCart([]);
      setSelectedClient('none');
      setCurrentPriceLevel('precio_1');
      setShipmentPin(null); // limpiar el pin del mapa
      // Volver a modo Venta y RESETEAR vendedor a Local (igual que tras una venta normal)
      setCartMode('sale');
      const local = (sellers || []).find(s => s.is_local) || (sellers || []).find(s => s.name === 'Local');
      setSelectedSeller(local ? local.id : '');
      setSellerLockedByQr(false);
      setScannedSellerName(null);
    } catch (error) {
      const raw = error?.response?.data?.detail;
      const msg = typeof raw === 'string'
        ? raw
        : Array.isArray(raw)
          ? (raw[0]?.msg || 'Datos inválidos al crear envío')
          : 'Error al crear envío';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedSeller) {
      toast.error('Selecciona un vendedor');
      return;
    }
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    const total = calculateTotal();

    // Validar pago mixto: permitir excedente (cambio en efectivo). Solo bloquear si paga MENOS.
    if (paymentMethod === 'mixto') {
      const totalPagado = paymentSplit.efectivo + paymentSplit.tarjeta + paymentSplit.transferencia;
      if (totalPagado + 0.01 < total) {
        toast.error(`Falta dinero por cobrar. Total: $${total.toFixed(2)}, recibido: $${totalPagado.toFixed(2)}`);
        return;
      }
      const cambio = totalPagado - total;
      if (cambio > 0.01) {
        // Confirmación rápida del cambio
        const ok = window.confirm(`Cambio a entregar (en efectivo): $${cambio.toFixed(2)}\n\n¿Confirmar venta?`);
        if (!ok) { return; }
      }
    }

    setLoading(true);
    try {
      const saleData = {
        seller_id: selectedSeller,
        client_id: selectedClient === 'none' ? null : selectedClient,
        items: cart,
        payment_method: paymentMethod,
        payment_split: paymentMethod === 'mixto' ? paymentSplit : null,
        total: total,
        has_shipment: false,
        shipment_cost: 0
      };

      await axios.post(`${API}/sales`, saleData, { withCredentials: true });

      toast.success('Venta registrada exitosamente');
      setCart([]);
      setSelectedClient('none');
      setCurrentPriceLevel('precio_1');
      setPaymentMethod('efectivo');
      setPaymentSplit({ efectivo: 0, tarjeta: 0, transferencia: 0 });
      setShowPaymentSplit(false);
      // Reset vendedor a Local + remover lock de QR para la siguiente venta
      const local = (sellers || []).find(s => s.is_local) || (sellers || []).find(s => s.name === 'Local');
      setSelectedSeller(local ? local.id : '');
      setSellerLockedByQr(false);
      setScannedSellerName(null);
      refetchAll();
      // Verificación cada 3 ventas: si está activa en la sucursal, el modal
      // se abrirá bloqueando el POS hasta que el cajero escriba la palabra.
      triggerSalesConfirm();
    } catch (error) {
      toast.error('Error al registrar venta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      {salesConfirmModal}
      <StaleCutWarning cartCount={cart.length} />
      <div className="p-3 sm:p-6 lg:p-8 h-full">
        <div className="flex justify-between items-center mb-4 lg:mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Punto de Venta</h1>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setEditDialog({ open: true, product: null, parent: null })}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="pos-new-product-btn"
            >
              <Plus size={18} weight="bold" className="mr-2" />
              Nuevo Producto
            </Button>
            <Button
              onClick={() => setExpenseDialog({ open: true, category: 'gasolina', amount: '', description: '', saving: false })}
              variant="outline"
              className="border-rose-500 text-rose-600 hover:bg-rose-50"
              data-testid="open-expense-button"
            >
              <MinusCircle size={18} weight="bold" className="mr-2" />
              Hacer gasto
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 lg:h-[calc(100vh-180px)]">
          {/* Left: Product Search & Cart */}
          <div className="lg:col-span-7 space-y-4 lg:overflow-auto">
            <div className="bg-white border border-border rounded-sm p-4">
              <div className="flex justify-between items-center">
                <Label>Buscar Producto</Label>
                <div className="text-xs px-2 py-1 rounded bg-primary/10 text-primary font-medium">
                  Nivel Activo: {currentPriceLevel.replace('_', ' ').replace('precio', 'Precio')}
                </div>
              </div>
              <div className="relative mt-1">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="product-search-input"
                  placeholder="Nombre o SKU... (incluye variantes)"
                  className="pl-10 pr-10"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    data-testid="product-search-clear"
                    aria-label="Limpiar búsqueda"
                    title="Limpiar búsqueda"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-muted-foreground hover:text-foreground transition"
                  >
                    <X size={16} weight="bold" />
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white border border-border rounded-sm p-4">
              <Label className="mb-2 block">Categorías</Label>
              <div className="flex gap-2 overflow-x-auto pb-1">
                <button
                  onClick={() => setSelectedCategory('all')}
                  data-testid="category-filter-all"
                  className={`flex-shrink-0 flex flex-col items-center gap-1 p-2 border rounded-sm btn-transition min-w-[80px] ${
                    selectedCategory === 'all' ? 'border-primary bg-primary/10' : 'border-border hover:bg-slate-50'
                  }`}
                >
                  <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center">
                    <span className="text-xs font-bold">Todas</span>
                  </div>
                  <span className="text-xs">Todas</span>
                </button>
                {parentCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    data-testid={`category-filter-${cat.id}`}
                    className={`flex-shrink-0 flex flex-col items-center gap-1 p-2 border rounded-sm btn-transition min-w-[80px] ${
                      selectedCategory === cat.id || (isSubcategory(selectedCategory) && categories.find((c) => c.id === selectedCategory)?.parent_id === cat.id)
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:bg-slate-50'
                    }`}
                  >
                    {cat.image_url ? (
                      <img src={cat.image_url} alt={cat.name} className="w-12 h-12 rounded object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded bg-slate-200 flex items-center justify-center">
                        <span className="text-xs font-bold text-slate-500">{cat.name.charAt(0)}</span>
                      </div>
                    )}
                    <span className="text-xs truncate w-full text-center">{cat.name}</span>
                  </button>
                ))}
                {parentCategories.length === 0 && (
                  <p className="text-xs text-muted-foreground self-center px-2">Sin categorías. Crea una en el menú Categorías.</p>
                )}
              </div>

              {activeSubcategories.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <Label className="mb-2 block text-xs text-muted-foreground">Subcategorías</Label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedCategory(activeParentId)}
                      data-testid="subcategory-filter-all"
                      className={`px-3 py-1.5 text-sm border rounded-full btn-transition ${
                        selectedCategory === activeParentId
                          ? 'border-primary bg-primary text-white'
                          : 'border-border hover:bg-slate-50'
                      }`}
                    >
                      Todas
                    </button>
                    {activeSubcategories.map((sub) => (
                      <button
                        key={sub.id}
                        onClick={() => setSelectedCategory(sub.id)}
                        data-testid={`subcategory-filter-${sub.id}`}
                        className={`px-3 py-1.5 text-sm border rounded-full btn-transition ${
                          selectedCategory === sub.id
                            ? 'border-primary bg-primary text-white'
                            : 'border-border hover:bg-slate-50'
                        }`}
                      >
                        {sub.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {availableBrands.length > 0 && false && (
                <div className="mt-3 pt-3 border-t">
                  <Label className="mb-2 block text-xs text-muted-foreground">Marcas</Label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedBrand('all')}
                      data-testid="brand-filter-all"
                      className={`px-3 py-1.5 text-sm border rounded-full btn-transition ${
                        selectedBrand === 'all'
                          ? 'border-primary bg-primary text-white'
                          : 'border-border hover:bg-slate-50'
                      }`}
                    >
                      Todas
                    </button>
                    {availableBrands.map((brand) => (
                      <button
                        key={brand}
                        onClick={() => setSelectedBrand(brand)}
                        data-testid={`brand-filter-${brand}`}
                        className={`px-3 py-1.5 text-sm border rounded-full btn-transition ${
                          selectedBrand === brand
                            ? 'border-primary bg-primary text-white'
                            : 'border-border hover:bg-slate-50'
                        }`}
                      >
                        {brand}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white border border-border rounded-sm p-4 space-y-2 max-h-[400px] overflow-y-auto" data-testid="products-list">
              {/* Paquetes mezclados con productos. Filtramos por categoría/marca/búsqueda igual que productos. */}
              {filteredPackages.map((pkg) => {
                const price = getPriceAtLevel(pkg, currentPriceLevel);
                const pkgPromoActive = isPromoActive(pkg);
                const pkgPromoPrice = !pkgPromoActive ? null
                  : pkg.promo_type === 'percent_discount' ? Math.round(price * (1 - Number(pkg.promo_discount_percent || 0) / 100) * 100) / 100
                  : ['flat', 'limited_qty'].includes(pkg.promo_type) || !pkg.promo_type ? Number(pkg.promo_price) : null;
                const pkgPromoLbl = pkgPromoActive
                  ? (['gift', 'free_shipping', 'combo', 'percent_discount', 'limited_qty'].includes(pkg.promo_type) ? promoBadgeLabel(pkg)
                    : pkg.promo_type === 'min_qty' && pkg.promo_min_qty ? `OFERTA · ≥${pkg.promo_min_qty}` : 'OFERTA')
                  : null;
                return (
                  <div
                    key={`pkg-${pkg.id}`}
                    className={`p-3 border rounded-sm hover:bg-slate-50 btn-transition relative ${pkgPromoActive ? 'border-rose-400 ring-1 ring-rose-200' : 'border-violet-300 bg-violet-50/30'}`}
                    data-testid={`pos-package-item-${pkg.id}`}
                  >
                    {pkgPromoActive && (
                      <div
                        className="absolute -top-2 -left-2 z-10 bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm shadow flex items-center gap-1"
                        title={pkg.promo_description || 'Promoción activa'}
                        data-testid={`package-promo-badge-${pkg.id}`}
                      >
                        <Tag size={10} weight="fill" /> {pkgPromoLbl}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowPackageDetail(pkg); }}
                      title="Ver contenido del paquete"
                      data-testid={`package-badge-${pkg.id}`}
                      className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-violet-600 text-white text-xs font-extrabold flex items-center justify-center shadow hover:bg-violet-700 hover:scale-110 btn-transition"
                    >
                      P
                    </button>
                    <div className="flex gap-3 items-start mb-2">
                      {pkg.image_url ? (
                        <img
                          src={pkg.image_url}
                          alt={pkg.name}
                          className="w-16 h-16 object-cover rounded cursor-zoom-in hover:opacity-80 btn-transition"
                          onClick={(e) => { e.stopPropagation(); setLightboxImage({ url: pkg.image_url, title: pkg.name }); }}
                          data-testid={`pkg-thumb-${pkg.id}`}
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                          <span className="text-xl">📦</span>
                        </div>
                      )}
                      <div className="flex-1 pr-8">
                        <p className="font-semibold text-sm">{pkg.name}</p>
                        {pkg.brand && (
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{pkg.brand}</p>
                        )}
                        <p className="text-xs text-muted-foreground">SKU: {pkg.sku}</p>
                        {pkgPromoActive && pkgPromoPrice !== null ? (
                          <div className="mt-1 flex items-baseline gap-2">
                            <p className="text-lg font-bold text-rose-600">${Number(pkgPromoPrice).toFixed(2)}</p>
                            <p className="text-xs text-slate-500 line-through">${parseFloat(price).toFixed(2)}</p>
                          </div>
                        ) : pkgPromoActive && pkg.promo_type === 'min_qty' ? (
                          <div className="mt-1">
                            <p className="text-lg font-bold text-primary">${parseFloat(price).toFixed(2)}</p>
                            <p className="text-[10px] text-rose-600 font-semibold">
                              🏷️ ${Number(pkg.promo_price).toFixed(2)} desde {pkg.promo_min_qty} pzas
                            </p>
                          </div>
                        ) : (
                          <p className="text-lg font-bold text-primary mt-1">${parseFloat(price).toFixed(2)}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => addPackageToCart(pkg)}
                      data-testid={`add-pos-package-${pkg.id}`}
                      className="w-full mt-2"
                    >
                      Agregar al Carrito
                    </Button>
                  </div>
                );
              })}

              {filteredProducts.map((product) => {
                const variantCount = product.has_variants ? getVariantsOf(product.id).length : 0;
                const showPromoBadge = parentHasActivePromo(product);
                const promoLbl = !product.has_variants ? promoBadgeLabel(product) : (showPromoBadge ? 'OFERTA' : null);
                const isGiftPromo = !product.has_variants && product.promo_type === 'gift' && isPromoActive(product);
                const promoPrice = !product.has_variants && isPromoActive(product) && !isGiftPromo ? product.promo_price : null;
                return (
                  <div
                    key={product.id}
                    className={`p-3 border rounded-sm hover:bg-slate-50 btn-transition relative ${showPromoBadge ? (isGiftPromo ? 'border-amber-400 ring-1 ring-amber-200' : 'border-rose-400 ring-1 ring-rose-200') : 'border-border'}`}
                    data-testid={`product-item-${product.id}`}
                  >
                    {showPromoBadge && (
                      <div
                        className={`absolute -top-2 -left-2 z-10 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm shadow flex items-center gap-1 ${isGiftPromo ? 'bg-amber-500' : 'bg-rose-500'}`}
                        data-testid={`promo-badge-${product.id}`}
                      >
                        {isGiftPromo ? <span>🎁</span> : <Tag size={10} weight="fill" />} {promoLbl}
                      </div>
                    )}
                    <div className="absolute top-2 right-2 z-10 flex gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setEditDialog({ open: true, product, parent: null }); }}
                        title="Editar producto"
                        data-testid={`edit-product-${product.id}`}
                        className="w-7 h-7 rounded-full bg-slate-200 hover:bg-amber-400 hover:text-white text-slate-700 flex items-center justify-center btn-transition"
                      >
                        <Pencil size={13} weight="bold" />
                      </button>
                    </div>
                    <div className="flex gap-3 items-start mb-2 pr-9">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded cursor-zoom-in hover:opacity-80 btn-transition"
                          onClick={(e) => { e.stopPropagation(); setLightboxImage({ url: product.image_url, title: product.name }); }}
                          data-testid={`prod-thumb-${product.id}`}
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                          <MagnifyingGlass size={24} className="text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        {product.parent_product_id && product.variant_name ? (
                          <>
                            <p className="font-semibold text-sm">
                              {product.name}
                              <span className="ml-1 text-primary font-bold">· {product.variant_name}</span>
                            </p>
                            <p className="text-[10px] text-amber-700 uppercase tracking-wide">Variante</p>
                          </>
                        ) : (
                          <>
                            <p className="font-semibold text-sm">{product.name}</p>
                            {product.brand && (
                              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{product.brand}</p>
                            )}
                          </>
                        )}
                        {product.has_variants && (
                          <p className="text-xs text-primary font-medium">
                            {variantCount} variantes disponibles
                            {showPromoBadge && <span className="ml-1 text-rose-600 font-bold">· con oferta</span>}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">SKU: {product.sku} | Stock: {product.stock}</p>
                        {!product.has_variants && (
                          promoPrice != null ? (
                            <div className="mt-1 flex items-baseline gap-2">
                              <span className="text-lg font-bold text-rose-600">${promoPrice.toFixed(2)}</span>
                              <span className="text-xs text-muted-foreground line-through">${getPriceAtLevel(product, currentPriceLevel).toFixed(2)}</span>
                            </div>
                          ) : (
                            <p className="text-lg font-bold text-primary mt-1">${getPriceAtLevel(product, currentPriceLevel).toFixed(2)}</p>
                          )
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleProductClick(product)}
                      data-testid={`add-${product.id}`}
                      className="w-full mt-2"
                    >
                      {product.has_variants ? 'Seleccionar Variante' : 'Agregar al Carrito'}
                    </Button>
                  </div>
                );
              })}
              {filteredProducts.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No se encontraron productos</p>
              )}
            </div>
          </div>

          {/* Right: Cart & Payment */}
          <div
            id="pos-cart-section"
            className={`lg:col-span-5 border-2 rounded-sm p-3 lg:p-6 flex flex-col btn-transition ${
            cartMode === 'shipment'
              ? 'bg-orange-50 border-orange-400'
              : 'bg-white border-border'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {cartMode === 'shipment' ? (
                  <Truck size={24} weight="bold" className="text-orange-600" />
                ) : (
                  <ShoppingCart size={24} weight="bold" />
                )}
                <h2 className={`text-xl font-semibold ${cartMode === 'shipment' ? 'text-orange-700' : ''}`}>
                  {cartMode === 'shipment' ? 'Modo Envío' : 'Carrito'}
                </h2>
              </div>
              {/* Mode toggle */}
              <div className="flex gap-1 bg-slate-100 p-1 rounded-full text-xs">
                <button
                  onClick={switchToSaleMode}
                  data-testid="mode-sale-button"
                  className={`px-3 py-1 rounded-full btn-transition ${
                    cartMode === 'sale' ? 'bg-white shadow-sm font-semibold' : 'text-muted-foreground'
                  }`}
                >
                  <ShoppingCart size={14} className="inline mr-1" />
                  Venta
                </button>
                <button
                  onClick={switchToShipmentMode}
                  data-testid="mode-shipment-button"
                  className={`px-3 py-1 rounded-full btn-transition ${
                    cartMode === 'shipment' ? 'bg-orange-500 text-white shadow-sm font-semibold' : 'text-muted-foreground'
                  }`}
                >
                  <Truck size={14} className="inline mr-1" />
                  Envío
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto mb-4 space-y-2">
              {cart.map((item, index) => (
                <div
                  key={index}
                  className="p-3 border border-border rounded-sm"
                  data-testid={`cart-item-${index}`}
                >
                  <div className="flex gap-3 items-start">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.product_name}
                        className="w-12 h-12 object-cover rounded flex-shrink-0 cursor-zoom-in hover:opacity-80 btn-transition"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (item.is_package && item.package_id) {
                            const pkg = (packages || []).find(p => p.id === item.package_id);
                            if (pkg) { setShowPackageDetail(pkg); return; }
                          }
                          setLightboxImage({ url: item.image_url, title: item.product_name });
                        }}
                        data-testid={`cart-thumb-${index}`}
                      />
                    ) : item.is_package && item.package_id ? (
                      <button
                        type="button"
                        className="w-12 h-12 bg-violet-100 rounded flex-shrink-0 flex items-center justify-center hover:bg-violet-200 btn-transition"
                        onClick={(e) => {
                          e.stopPropagation();
                          const pkg = (packages || []).find(p => p.id === item.package_id);
                          if (pkg) setShowPackageDetail(pkg);
                        }}
                        title="Ver contenido del paquete"
                        data-testid={`cart-pkg-btn-${index}`}
                      >
                        <span className="text-lg">📦</span>
                      </button>
                    ) : (
                      <div className="w-12 h-12 bg-slate-100 rounded flex-shrink-0 flex items-center justify-center">
                        <MagnifyingGlass size={16} className="text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.product_name}</p>
                      <p className="text-xs text-muted-foreground">{item.price_type.replace('_', ' ')}</p>
                      {item.is_promo && (
                        <span className="inline-flex items-center gap-0.5 mt-0.5 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                          <Tag size={10} weight="fill" /> PROMO
                        </span>
                      )}
                      {item.manual_price && !item.is_promo && (
                        <span className="inline-flex items-center gap-0.5 mt-0.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                          <Pencil size={10} /> Precio manual
                        </span>
                      )}
                      {pendingApprovals[index] && (
                        <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded animate-pulse">
                          ⏳ Esperando aprobación…
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openPriceChangeDialog(index)}
                      data-testid={`change-price-${index}`}
                      title="Cambiar precio (requiere aprobación)"
                      disabled={!!pendingApprovals[index]}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeFromCart(index)}
                      data-testid={`remove-cart-item-${index}`}
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(index, parseInt(e.target.value))}
                      data-testid={`cart-quantity-${index}`}
                      className="w-20"
                    />
                    {promoOverrides[item.product_id || item.package_id] !== undefined && !item.manual_price ? (
                      <span className="text-sm flex items-center gap-1" data-testid={`cart-promo-${index}`}>
                        <span className="line-through text-slate-400">${item.unit_price.toFixed(2)}</span>
                        <span className="font-bold text-purple-700">${Number(promoOverrides[item.product_id || item.package_id]).toFixed(2)}</span>
                        <span className="text-[9px] font-bold bg-purple-100 text-purple-700 rounded px-1">🎉 PROMO</span>
                      </span>
                    ) : (
                      <span className="text-sm">x ${item.unit_price.toFixed(2)}</span>
                    )}
                    <span className="ml-auto font-semibold">${((item.manual_price ? item.unit_price : (promoOverrides[item.product_id || item.package_id] !== undefined ? promoOverrides[item.product_id || item.package_id] : item.unit_price)) * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              ))}
              {cart.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Carrito vacío</p>
              )}
              {/* Sugerencias de regalos disponibles */}
              {comboSuggestions.map(p => (
                <div key={p.id} className="mt-2 text-xs bg-orange-50 border border-orange-300 text-orange-800 rounded p-2 flex items-center justify-between gap-2" data-testid={`combo-suggestion-${p.id}`}>
                  <span>🔗 Agrega <b>{p.name}</b> y ahorra <b>${Number(p.promo_combo_discount).toFixed(0)}</b></span>
                  <button type="button" onClick={() => (p.is_package ? addPackageToCart(p) : addToCart(p))}
                    className="shrink-0 px-2 py-1 rounded bg-orange-500 hover:bg-orange-600 text-white font-semibold" data-testid={`combo-suggestion-add-${p.id}`}>
                    + Agregar
                  </button>
                </div>
              ))}
              {comboWarnings.length > 0 && (
                <div className="mt-2 text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded p-2" data-testid="combo-warning">
                  ⚠️ La promo combo de <b>{comboWarnings.join(', ')}</b> solo aplica a 1 pieza por venta. Con 2 o más no se aplica: cobra las demás en otra venta.
                </div>
              )}
              {cart.length > 0 && promoHints.length > 0 && (
                <div className="mt-2 space-y-1">
                  {promoHints.map(h => {
                    const trigger = cart.find(it => it.product_id === h.id);
                    const triggerQty = trigger ? Number(trigger.quantity || 0) : 1;
                    const giftPerUnit = Number(h.promo_gift_qty || 1);
                    const totalGiftQty = Math.max(1, triggerQty * giftPerUnit);
                    const giftProduct = products.find(p => p.id === h.promo_gift_product_id);
                    return (
                      <div key={h.id} className="bg-pink-50 border border-pink-300 text-pink-800 rounded-lg px-3 py-2 text-xs flex items-center gap-2" data-testid={`promo-hint-${h.id}`}>
                        <span className="text-lg">🎁</span>
                        <span className="flex-1">
                          Puedes agregar <b>{totalGiftQty}× {h.promo_gift_product_name || 'regalo'}</b> gratis con <b>{h.product_name || h.name || 'este producto'}</b>
                        </span>
                        {giftProduct && (
                          <button
                            type="button"
                            onClick={() => {
                              // Agregar el producto regalo. El backend aplica precio 0 automáticamente
                              // vía /promotions/preview (matched por promo_gift_product_id).
                              const commission = giftProduct.default_commission || 0;
                              const existing = cart.find(it => it.product_id === giftProduct.id);
                              if (existing) {
                                setCart(cart.map(it =>
                                  it.product_id === giftProduct.id
                                    ? { ...it, quantity: totalGiftQty }
                                    : it
                                ));
                              } else {
                                setCart([...cart, {
                                  product_id: giftProduct.id,
                                  product_name: giftProduct.variant_name ? `${giftProduct.name} (${giftProduct.variant_name})` : giftProduct.name,
                                  quantity: totalGiftQty,
                                  price_type: currentPriceLevel,
                                  unit_price: Number(giftProduct.precio_1 || 0),
                                  original_price: Number(giftProduct.precio_1 || 0),
                                  commission,
                                  image_url: giftProduct.image_url || null,
                                  is_promo: true,
                                  promo_min_qty: null,
                                  manual_price: false,
                                }]);
                              }
                              toast.success(`🎁 ${giftProduct.name} agregado como regalo`);
                            }}
                            className="bg-pink-500 hover:bg-pink-600 text-white text-[10px] font-bold px-2.5 py-1 rounded shadow"
                            data-testid={`add-gift-${h.id}`}
                          >
                            Agregar regalo
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <div>
                <Label>Vendedor *</Label>
                {cartMode === 'sale' && !allowManualSeller ? (
                  <div className="border border-border rounded-sm px-3 py-2 bg-slate-50 flex items-center justify-between" data-testid="seller-locked-display">
                    <div>
                      <p className="text-sm font-medium" data-testid="seller-locked-name">
                        {(() => {
                          const s = (sellers || []).find(x => x.id === selectedSeller);
                          return s ? s.name : 'Local';
                        })()}
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {sellerLockedByQr
                          ? '🔒 Vendedor confirmado por QR — no se puede cambiar'
                          : 'Por defecto: Local. Escanea el código QR del vendedor para asignarlo.'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {sellerLockedByQr && (
                        <>
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-1 rounded font-semibold">QR ✓</span>
                          <button
                            type="button"
                            onClick={() => setClearSellerDialog({ open: true, action: 'cleared', reason: '', saving: false })}
                            data-testid="clear-seller-btn"
                            className="text-[11px] text-rose-600 hover:bg-rose-50 px-2 py-1 rounded border border-rose-200 btn-transition"
                          >
                            Limpiar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <Select value={selectedSeller} onValueChange={(v) => { setSelectedSeller(v); setScannedSellerName(null); setSellerLockedByQr(false); }}>
                    <SelectTrigger data-testid="seller-select">
                      <SelectValue placeholder="Selecciona vendedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {sellers.map((seller) => (
                        <SelectItem key={seller.id} value={seller.id}>
                          {seller.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label>Cliente (Opcional)</Label>
                <Select value={selectedClient} onValueChange={handleClientChange}>
                  <SelectTrigger data-testid="client-select">
                    <SelectValue placeholder="Selecciona cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cliente (Precio 1)</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} - {client.precio_nivel?.replace('_', ' ') || 'Precio 1'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {cartMode === 'sale' && (
                <>
                  <div>
                    <Label>Método de Pago</Label>
                    <Select value={paymentMethod} onValueChange={(value) => {
                      setPaymentMethod(value);
                      if (value === 'mixto') {
                        setShowPaymentSplit(true);
                        setPaymentSplit({ efectivo: calculateTotal(), tarjeta: 0, transferencia: 0 });
                      } else {
                        setShowPaymentSplit(false);
                      }
                    }}>
                      <SelectTrigger data-testid="payment-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                        <SelectItem value="mixto">Mixto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {showPaymentSplit && (
                    <div className="space-y-3 p-4 border border-border rounded">
                      <p className="text-sm font-medium">Dividir Pago</p>
                      <div>
                        <Label htmlFor="split-efectivo">Efectivo</Label>
                        <Input
                          id="split-efectivo"
                          type="number"
                          step="0.01"
                          value={paymentSplit.efectivo}
                          onChange={(e) => setPaymentSplit({ ...paymentSplit, efectivo: parseFloat(e.target.value) || 0 })}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label htmlFor="split-transferencia">Transferencia</Label>
                        <Input
                          id="split-transferencia"
                          type="number"
                          step="0.01"
                          value={paymentSplit.transferencia}
                          onChange={(e) => setPaymentSplit({ ...paymentSplit, transferencia: parseFloat(e.target.value) || 0 })}
                          placeholder="0.00"
                        />
                      </div>
                      <div className="pt-2 border-t">
                        <div className="flex justify-between text-sm">
                          <span>Total Pagado:</span>
                          <span className="font-semibold">
                            ${(paymentSplit.efectivo + paymentSplit.tarjeta + paymentSplit.transferencia).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span>Total Venta:</span>
                          <span className="font-semibold">${calculateTotal().toFixed(2)}</span>
                        </div>
                        {(() => {
                          const pagado = paymentSplit.efectivo + paymentSplit.tarjeta + paymentSplit.transferencia;
                          const total = calculateTotal();
                          const diff = pagado - total;
                          if (diff > 0.01) {
                            return (
                              <div className="flex justify-between text-sm mt-1 text-emerald-700 font-bold bg-emerald-50 px-2 py-1 rounded" data-testid="cambio-display">
                                <span>💵 Cambio (en efectivo):</span>
                                <span>${diff.toFixed(2)}</span>
                              </div>
                            );
                          }
                          if (diff < -0.01) {
                            return (
                              <div className="flex justify-between text-sm mt-1 text-rose-700 font-bold bg-rose-50 px-2 py-1 rounded" data-testid="falta-display">
                                <span>⚠️ Falta:</span>
                                <span>${Math.abs(diff).toFixed(2)}</span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  )}
                </>
              )}

              {cartMode === 'shipment' && (
                <div className="space-y-3 p-3 bg-orange-100 border border-orange-300 rounded">
                  <p className="text-sm font-semibold text-orange-700 flex items-center gap-1">
                    <Truck size={16} /> Datos del Envío (todos obligatorios)
                  </p>
                  {nextFolio && (
                    <div className="flex items-center justify-between bg-white border border-orange-400 rounded px-3 py-2" data-testid="shipment-next-folio">
                      <span className="text-xs text-orange-700 uppercase font-semibold tracking-wide">Folio asignado</span>
                      <span className="text-2xl font-bold font-mono text-orange-900">{nextFolio}</span>
                    </div>
                  )}
                  <div>
                    <Label>Nombre del Cliente *</Label>
                    <Input
                      value={shipmentForm.client_name}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, client_name: e.target.value })}
                      data-testid="shipment-client-name-input"
                    />
                  </div>
                  <div>
                    <Label>Teléfono *</Label>
                    <Input
                      value={shipmentForm.client_phone}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, client_phone: e.target.value })}
                      data-testid="shipment-client-phone-input"
                      placeholder="Ej: 5551234567"
                    />
                  </div>
                  <div>
                    <Label>📍 Ubicación en mapa <span className="text-[10px] text-muted-foreground font-normal">(recomendado — el entregador la usará para la ruta)</span></Label>
                    <div className="mt-1">
                      <AddressPicker
                        value={shipmentPin}
                        onChange={(v) => {
                          setShipmentPin(v);
                          // Autopoblar la colonia si está vacía
                          if (v?.colonia && !shipmentForm.shipping_address?.trim()) {
                            setShipmentForm((f) => ({ ...f, shipping_address: v.colonia }));
                          }
                        }}
                        center={[-100.94424590860048, 25.42114314472833]}
                        height={220}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Colonia *</Label>
                    <Input
                      value={shipmentForm.shipping_address}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, shipping_address: e.target.value })}
                      data-testid="shipment-address-input"
                      placeholder="Ej: Mirasierra"
                    />
                    {/* Quote inline */}
                    {coloniaQuote.loading && (
                      <p className="text-[11px] text-muted-foreground mt-1">Buscando precio…</p>
                    )}
                    {!coloniaQuote.loading && coloniaQuote.match && coloniaQuote.lastQuery && (
                      coloniaQuote.match.quote_with_store ? (
                        <div className="mt-1 px-2 py-1 bg-amber-100 border border-amber-300 rounded text-xs font-semibold text-amber-800" data-testid="colonia-quote-store">
                          📞 {coloniaQuote.match.colonia} — Cotizar con Tienda
                        </div>
                      ) : coloniaQuote.match.best_price != null ? (
                        <div className="mt-1 px-2 py-1 bg-emerald-50 border border-emerald-300 rounded text-xs flex items-center justify-between" data-testid="colonia-quote-match">
                          <span className="font-semibold text-emerald-800">✓ {coloniaQuote.match.colonia}{coloniaQuote.match.km != null && coloniaQuote.match.km > 0 ? ` · ${coloniaQuote.match.km} km` : ''}</span>
                          <span className="font-bold text-emerald-700">${coloniaQuote.match.best_price.toFixed(2)}</span>
                        </div>
                      ) : null
                    )}
                    {!coloniaQuote.loading && !coloniaQuote.match && coloniaQuote.suggestions.length > 0 && (
                      <div className="mt-1 space-y-1" data-testid="colonia-quote-suggestions">
                        <p className="text-[11px] text-muted-foreground">¿Quisiste decir?</p>
                        {coloniaQuote.suggestions.slice(0, 4).map((s) => (
                          <button
                            type="button"
                            key={s.id}
                            onClick={() => applyColoniaSuggestion(s)}
                            className="w-full flex items-center justify-between text-xs px-2 py-1 border rounded hover:bg-slate-50 text-left"
                            data-testid={`colonia-suggestion-${s.id}`}
                          >
                            <span className="font-medium">{s.colonia}{s.km != null && s.km > 0 ? ` · ${s.km} km` : ''}</span>
                            <span className="font-semibold">
                              {s.quote_with_store ? '📞 Cotizar' : (s.best_price != null ? `$${s.best_price.toFixed(2)}` : '—')}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Costo de Envío *</Label>
                    {freeShippingNames.length > 0 && (
                      <div className="mb-1 text-xs font-semibold text-teal-800 bg-teal-50 border border-teal-300 rounded px-2 py-1" data-testid="free-shipping-banner">
                        🚚 Envío gratis por: {freeShippingNames.join(', ')} — deja el costo en $0
                      </div>
                    )}
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={shipmentForm.shipping_cost}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, shipping_cost: e.target.value })}
                      data-testid="shipment-cost-input"
                    />
                  </div>
                  <div>
                    <Label>Notas (Opcional)</Label>
                    <Input
                      value={shipmentForm.notes}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, notes: e.target.value })}
                      data-testid="shipment-notes-input"
                    />
                  </div>
                </div>
              )}

              <div className={`p-4 rounded-sm space-y-2 ${cartMode === 'shipment' ? 'bg-orange-100' : 'bg-slate-50'}`}>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Comisión Vendedor:</span>
                  <span className="font-semibold text-emerald-600" data-testid="cart-commission">
                    ${calculateTotalCommission().toFixed(2)}
                  </span>
                </div>
                {cartMode === 'shipment' && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span>Productos:</span>
                      <span>${calculateTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Costo Envío:</span>
                      <span>${(parseFloat(shipmentForm.shipping_cost) || 0).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className={`flex justify-between text-2xl font-bold border-t pt-2 ${cartMode === 'shipment' ? 'text-orange-700' : ''}`}>
                  <span>Total:</span>
                  <span data-testid="cart-total">
                    ${(calculateTotal() + (cartMode === 'shipment' ? (parseFloat(shipmentForm.shipping_cost) || 0) : 0)).toFixed(2)}
                  </span>
                </div>
              </div>

              {cartMode === 'sale' ? (
                <div className="space-y-2">
                  <Button
                    onClick={handleCheckout}
                    disabled={loading || cart.length === 0 || !selectedSeller || Object.keys(pendingApprovals).length > 0}
                    data-testid="checkout-button"
                    className="w-full"
                    size="lg"
                  >
                    {loading ? 'Procesando...' : 'Completar Venta'}
                  </Button>
                  <Button
                    onClick={() => {
                      if (!selectedSeller) { toast.error('Selecciona vendedor'); return; }
                      if (cart.length === 0) { toast.error('Carrito vacío'); return; }
                      setApartadoDialog({
                        open: true,
                        client_name: '',
                        client_phone: '',
                        due_date: '',
                        initial_payment: '',
                        initial_payment_method: 'efectivo',
                        deliver_now: false,
                        notes: '',
                        submitting: false,
                      });
                    }}
                    disabled={loading || cart.length === 0 || !selectedSeller}
                    data-testid="apartado-button"
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                    size="lg"
                  >
                    📋 Apartado
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => submitShipment()}
                  disabled={loading || cart.length === 0 || !selectedSeller || !!lowCostWait}
                  data-testid="submit-shipment-button"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  size="lg"
                >
                  <Truck size={20} className="mr-2" />
                  {lowCostWait ? '⏳ Esperando autorización del envío…' : loading ? 'Procesando...' : 'Crear Envío'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Variant Selection Modal */}
      <Dialog open={!!variantModalProduct} onOpenChange={(open) => !open && setVariantModalProduct(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <span>Seleccionar Variante: {variantModalProduct?.name}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline"
                  onClick={() => setEditDialog({ open: true, product: variantModalProduct, parent: null })}
                  data-testid="edit-parent-from-variant-modal">
                  <Pencil size={14} className="mr-1" /> Editar padre
                </Button>
                <Button size="sm"
                  onClick={() => setEditDialog({ open: true, product: null, parent: variantModalProduct })}
                  data-testid="add-variant-from-modal"
                  className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus size={14} className="mr-1" /> Variante
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
            {variantModalProduct && getVariantsOf(variantModalProduct.id).map((variant) => {
              const vPromoActive = isPromoActive(variant);
              const vPromoLbl = promoBadgeLabel(variant);
              return (
              <div
                key={variant.id}
                className={`p-3 border rounded-sm hover:bg-slate-50 btn-transition relative ${vPromoActive ? 'border-rose-400 ring-1 ring-rose-200' : 'border-border'}`}
                data-testid={`variant-item-${variant.id}`}
              >
                {vPromoActive && (
                  <div
                    className="absolute -top-2 -left-2 z-10 bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm shadow flex items-center gap-1"
                    data-testid={`variant-promo-badge-${variant.id}`}
                  >
                    <Tag size={10} weight="fill" /> {vPromoLbl}
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setEditDialog({ open: true, product: variant, parent: variantModalProduct }); }}
                  title="Editar variante"
                  data-testid={`edit-variant-${variant.id}`}
                  className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-slate-200 hover:bg-amber-400 hover:text-white text-slate-700 flex items-center justify-center btn-transition"
                >
                  <Pencil size={13} weight="bold" />
                </button>
                <div className="flex gap-3 items-start mb-2 pr-8">
                  {variant.image_url ? (
                    <img
                      src={variant.image_url}
                      alt={variant.variant_name}
                      className="w-16 h-16 object-cover rounded cursor-zoom-in hover:opacity-80 btn-transition"
                      onClick={(e) => { e.stopPropagation(); setLightboxImage({ url: variant.image_url, title: `${variant.name} – ${variant.variant_name}` }); }}
                      data-testid={`variant-thumb-${variant.id}`}
                    />
                  ) : (
                    <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                      <MagnifyingGlass size={24} className="text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{variant.variant_name}</p>
                    <p className="text-xs text-muted-foreground">SKU: {variant.sku} | Stock: {variant.stock}</p>
                    {vPromoActive && variant.promo_type === 'flat' ? (
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-lg font-bold text-rose-600">${variant.promo_price?.toFixed(2)}</span>
                        <span className="text-xs text-muted-foreground line-through">${getPriceAtLevel(variant, currentPriceLevel).toFixed(2)}</span>
                      </div>
                    ) : (
                      <p className="text-lg font-bold text-primary mt-1">${getPriceAtLevel(variant, currentPriceLevel).toFixed(2)}</p>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    addToCart(variant);
                    setVariantModalProduct(null);
                  }}
                  disabled={!allowNegativeStock && variant.stock <= 0}
                  data-testid={`add-variant-${variant.id}`}
                  className="w-full"
                >
                  {variant.stock <= 0 ? (allowNegativeStock ? 'Agregar (sin stock)' : 'Sin Stock') : 'Agregar'}
                </Button>
              </div>
            );})}
            {variantModalProduct && getVariantsOf(variantModalProduct.id).length === 0 && (
              <p className="col-span-full text-center text-muted-foreground py-8">
                Este producto no tiene variantes registradas.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Package detail dialog (badge "P" click) */}
      <Dialog open={!!showPackageDetail} onOpenChange={(o) => !o && setShowPackageDetail(null)}>
        <DialogContent className="max-w-md" data-testid="package-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-violet-600 text-white text-xs font-extrabold">P</span>
              Contenido del paquete
            </DialogTitle>
          </DialogHeader>
          {showPackageDetail && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {showPackageDetail.image_url ? (
                  <img
                    src={showPackageDetail.image_url}
                    alt={showPackageDetail.name}
                    className="w-14 h-14 object-cover rounded cursor-zoom-in hover:opacity-80 btn-transition"
                    onClick={() => setLightboxImage({ url: showPackageDetail.image_url, title: showPackageDetail.name })}
                    data-testid="pkg-detail-hero"
                  />
                ) : (
                  <div className="w-14 h-14 bg-violet-100 rounded flex items-center justify-center"><span className="text-xl">📦</span></div>
                )}
                <div>
                  <p className="font-semibold">{showPackageDetail.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{showPackageDetail.sku}</p>
                </div>
              </div>
              <div className="border border-border rounded-sm divide-y divide-border">
                {(showPackageDetail.components || []).length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">Este paquete no tiene componentes.</p>
                ) : (
                  showPackageDetail.components.map((c, i) => {
                    // Buscar imagen del componente desde el listado de productos ya cargado
                    const compProd = (products || []).find(p => p.id === c.product_id);
                    const compImg = compProd?.image_url;
                    const compTitle = c.variant_name ? `${c.name} – ${c.variant_name}` : c.name;
                    return (
                      <div key={i} className="p-2 flex items-center gap-3 text-sm">
                        {compImg ? (
                          <img
                            src={compImg}
                            alt={compTitle}
                            className="w-12 h-12 object-cover rounded flex-shrink-0 cursor-zoom-in hover:opacity-80 btn-transition"
                            onClick={() => setLightboxImage({ url: compImg, title: compTitle })}
                            data-testid={`pkg-comp-thumb-${i}`}
                          />
                        ) : (
                          <div className="w-12 h-12 bg-slate-100 rounded flex-shrink-0 flex items-center justify-center">
                            <MagnifyingGlass size={14} className="text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{compTitle}</p>
                          <p className="text-xs text-muted-foreground font-mono">{c.sku}</p>
                        </div>
                        <span className="bg-violet-50 text-violet-700 font-semibold text-xs px-2 py-1 rounded flex-shrink-0">×{c.quantity}</span>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                Comisión del paquete: <span className="font-semibold text-emerald-600">${parseFloat(showPackageDetail.default_commission || 0).toFixed(2)}</span>
              </div>
              <Button className="w-full" onClick={() => { addPackageToCart(showPackageDetail); setShowPackageDetail(null); }} data-testid="package-detail-add-btn">
                Agregar al Carrito
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Price change dialog */}
      <Dialog open={priceChangeDialog.open} onOpenChange={(o) => !o && setPriceChangeDialog({ open: false, index: null, newPrice: '', reason: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil size={20} />
              Cambiar precio
            </DialogTitle>
          </DialogHeader>
          {priceChangeDialog.index !== null && cart[priceChangeDialog.index] && (
            <div className="space-y-4">
              <div className="bg-slate-50 border rounded p-3 text-sm">
                <p className="font-medium">{cart[priceChangeDialog.index].product_name}</p>
                <p className="text-muted-foreground">Precio actual: <span className="font-semibold text-foreground">${cart[priceChangeDialog.index].unit_price.toFixed(2)}</span> · Cantidad: {cart[priceChangeDialog.index].quantity}</p>
              </div>
              <div>
                <Label htmlFor="new-price">Nuevo precio *</Label>
                <Input
                  id="new-price"
                  type="number"
                  step="0.01"
                  value={priceChangeDialog.newPrice}
                  onChange={(e) => setPriceChangeDialog((d) => ({ ...d, newPrice: e.target.value }))}
                  data-testid="new-price-input"
                  autoFocus
                />
              </div>
              {!isAdmin && (
                <div>
                  <Label htmlFor="reason">Motivo del cambio *</Label>
                  <Input
                    id="reason"
                    placeholder="Ej: Cliente frecuente, error de etiquetado..."
                    value={priceChangeDialog.reason}
                    onChange={(e) => setPriceChangeDialog((d) => ({ ...d, reason: e.target.value }))}
                    data-testid="price-change-reason-input"
                  />
                  <p className="text-xs text-amber-600 mt-1">
                    ⏳ Este cambio requerirá aprobación del admin por Telegram.
                  </p>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setPriceChangeDialog({ open: false, index: null, newPrice: '', reason: '' })}>
                  Cancelar
                </Button>
                <Button onClick={submitPriceChange} data-testid="submit-price-change">
                  {isAdmin ? 'Aplicar' : 'Solicitar aprobación'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ExpenseDialog open={expenseDialog.open} onClose={() => setExpenseDialog(d => ({ ...d, open: false }))} />

      {/* Hidden admin shortcut dialog: Ctrl+Shift+V → asignar vendedor manual */}
      <Dialog
        open={adminAssignSeller.open}
        onOpenChange={(o) => !o && setAdminAssignSeller({ open: false, step: 'password', password: '', sellerId: '', saving: false })}
      >
        <DialogContent className="max-w-md" data-testid="admin-assign-seller-dialog">
          <DialogHeader>
            <DialogTitle>
              {adminAssignSeller.step === 'password' ? 'Acceso restringido' : 'Asignar vendedor'}
            </DialogTitle>
          </DialogHeader>
          {adminAssignSeller.step === 'password' ? (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Ingresa la contraseña para continuar.
              </p>
              <Input
                type="password"
                value={adminAssignSeller.password}
                onChange={(e) => setAdminAssignSeller(s => ({ ...s, password: e.target.value }))}
                placeholder="Contraseña"
                autoFocus
                data-testid="admin-assign-password"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && adminAssignSeller.password && !adminAssignSeller.saving) {
                    e.preventDefault();
                    setAdminAssignSeller(s => ({ ...s, saving: true }));
                    try {
                      await axios.post(`${API}/auth/verify-admin-password`, { password: adminAssignSeller.password }, { withCredentials: true });
                      setAdminAssignSeller(s => ({ ...s, step: 'seller', password: '', saving: false }));
                    } catch (err) {
                      toast.error('Contraseña incorrecta');
                      setAdminAssignSeller(s => ({ ...s, password: '', saving: false }));
                    }
                  }
                }}
              />
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="ghost"
                  onClick={() => setAdminAssignSeller({ open: false, step: 'password', password: '', sellerId: '', saving: false })}
                >
                  Cancelar
                </Button>
                <Button
                  disabled={!adminAssignSeller.password || adminAssignSeller.saving}
                  onClick={async () => {
                    setAdminAssignSeller(s => ({ ...s, saving: true }));
                    try {
                      await axios.post(`${API}/auth/verify-admin-password`, { password: adminAssignSeller.password }, { withCredentials: true });
                      setAdminAssignSeller(s => ({ ...s, step: 'seller', password: '', saving: false }));
                    } catch (err) {
                      toast.error('Contraseña incorrecta');
                      setAdminAssignSeller(s => ({ ...s, password: '', saving: false }));
                    }
                  }}
                  data-testid="admin-assign-password-submit"
                >
                  {adminAssignSeller.saving ? 'Verificando…' : 'Continuar'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Selecciona al vendedor a asignar. Quedará bloqueado para esta venta como si se hubiera escaneado el QR.
              </p>
              <Select
                value={adminAssignSeller.sellerId}
                onValueChange={(v) => setAdminAssignSeller(s => ({ ...s, sellerId: v }))}
              >
                <SelectTrigger data-testid="admin-assign-seller-select"><SelectValue placeholder="Vendedor…" /></SelectTrigger>
                <SelectContent>
                  {(sellers || [])
                    .filter(s => !s.is_local && s.name !== 'Local')
                    .map(s => (
                      <SelectItem key={s.id} value={s.id} data-testid={`admin-assign-seller-${s.id}`}>{s.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="ghost"
                  onClick={() => setAdminAssignSeller({ open: false, step: 'password', password: '', sellerId: '', saving: false })}
                >
                  Cancelar
                </Button>
                <Button
                  disabled={!adminAssignSeller.sellerId}
                  onClick={() => {
                    const sel = (sellers || []).find(s => s.id === adminAssignSeller.sellerId);
                    if (!sel) return;
                    setSelectedSeller(sel.id);
                    setScannedSellerName(sel.name);
                    if (cartMode === 'sale') setSellerLockedByQr(true);
                    toast.success(`Vendedor asignado: ${sel.name}`);
                    setAdminAssignSeller({ open: false, step: 'password', password: '', sellerId: '', saving: false });
                  }}
                  data-testid="admin-assign-seller-confirm"
                >
                  Asignar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo: Promos activas + Notificar a vendedores */}
      <Dialog
        open={promoNotifyDialog.open}
        onOpenChange={(o) => !o && !promoNotifyDialog.sending && setPromoNotifyDialog({ open: false, selectedIds: new Set(), sending: false, promos: [], loadingPromos: false })}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="promo-notify-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone size={22} weight="bold" className="text-amber-600" />
              Promos activas
            </DialogTitle>
          </DialogHeader>

          {/* Lista de promos */}
          <div className="border rounded-md bg-amber-50/40">
            <div className="px-3 py-2 border-b bg-amber-50 text-xs font-semibold text-amber-900 flex items-center justify-between">
              <span>
                {promoNotifyDialog.loadingPromos
                  ? 'Cargando promos…'
                  : `${promoNotifyDialog.promos.length} ${promoNotifyDialog.promos.length === 1 ? 'promoción activa' : 'promociones activas'}`}
              </span>
            </div>
            {promoNotifyDialog.loadingPromos ? (
              <p className="p-3 text-xs text-muted-foreground">Cargando…</p>
            ) : promoNotifyDialog.promos.length === 0 ? (
              <p className="p-3 text-xs text-muted-foreground">No hay promociones activas en este momento.</p>
            ) : (
              <div className="max-h-56 overflow-y-auto divide-y" data-testid="promo-active-list">
                {promoNotifyDialog.promos.map((p) => {
                  const nm = p.variant_name || p.name || '—';
                  const promoP = Number(p.promo_price || 0);
                  const baseP = Number(p.precio_1 || 0);
                  const savings = baseP > promoP && promoP > 0;
                  const scope = p.promo_local_only ? 'Solo local' : 'Local y envío';
                  return (
                    <div key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`promo-active-${p.id}`}>
                      {p.image_url && (
                        <img src={p.image_url} alt={nm} className="w-10 h-10 object-cover rounded border" loading="lazy" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">🏷️ {nm}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {scope} · stock {Number(p.stock || 0)}
                          {p.promo_type === 'min_qty' && p.promo_min_qty ? ` · desde ${Number(p.promo_min_qty)} pzas` : ''}
                          {p.promo_end_date ? ` · vence ${String(p.promo_end_date).slice(0, 10)}` : ''}
                        </div>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <div className="font-bold text-amber-700">${promoP.toFixed(2)}</div>
                        {savings && <div className="text-[10px] text-muted-foreground line-through">${baseP.toFixed(2)}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selector de vendedores + envío */}
          <div className="mt-2">
            <p className="text-xs text-muted-foreground mb-2">
              Notifica por Telegram la lista de promos a los vendedores seleccionados (igual que el comando <code>/promos</code>).
            </p>
          {(() => {
            const eligible = (sellers || []).filter(s => !s.is_local && s.telegram_chat_id);
            const noTg = (sellers || []).filter(s => !s.is_local && !s.telegram_chat_id);
            if (eligible.length === 0) {
              return (
                <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded p-3">
                  Ningún vendedor tiene Telegram vinculado. Pídeles correr <code>/vincular CODIGO</code> en el bot primero.
                </p>
              );
            }
            const allSelected = eligible.every(s => promoNotifyDialog.selectedIds.has(s.id));
            return (
              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">{promoNotifyDialog.selectedIds.size} de {eligible.length} seleccionados</span>
                  <button
                    type="button"
                    className="text-violet-700 hover:underline"
                    onClick={() => {
                      if (allSelected) setPromoNotifyDialog(s => ({ ...s, selectedIds: new Set() }));
                      else setPromoNotifyDialog(s => ({ ...s, selectedIds: new Set(eligible.map(x => x.id)) }));
                    }}
                    data-testid="promo-notify-select-all"
                  >
                    {allSelected ? 'Quitar todos' : 'Notificar a todos'}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto border rounded divide-y">
                  {eligible.map(s => {
                    const checked = promoNotifyDialog.selectedIds.has(s.id);
                    return (
                      <label
                        key={s.id}
                        className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer text-sm"
                        data-testid={`promo-notify-seller-${s.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setPromoNotifyDialog(prev => {
                              const next = new Set(prev.selectedIds);
                              if (e.target.checked) next.add(s.id); else next.delete(s.id);
                              return { ...prev, selectedIds: next };
                            });
                          }}
                        />
                        <span className="flex-1">{s.name}</span>
                        <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">TG ✓</span>
                      </label>
                    );
                  })}
                </div>
                {noTg.length > 0 && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    ⚠️ {noTg.length} vendedor(es) sin Telegram: {noTg.map(s => s.name).join(', ')}
                  </p>
                )}
              </div>
            );
          })()}
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => setPromoNotifyDialog({ open: false, selectedIds: new Set(), sending: false, promos: [], loadingPromos: false })}
              disabled={promoNotifyDialog.sending}
            >
              Cerrar
            </Button>
            <Button
              variant="outline"
              disabled={promoNotifyDialog.selectedIds.size === 0 || promoNotifyDialog.sending}
              onClick={async () => {
                setPromoNotifyDialog(s => ({ ...s, sending: true }));
                try {
                  const { data } = await axios.post(
                    `${API}/promos/notify-sellers`,
                    { seller_ids: Array.from(promoNotifyDialog.selectedIds), text_only: true },
                    { withCredentials: true }
                  );
                  toast.success(`📝 Resumen (sin fotos) enviado a ${data.sent} vendedor(es)`);
                  if (data.errors && data.errors.length > 0) {
                    toast.warning(`Hubo ${data.errors.length} error(es).`);
                  }
                  setPromoNotifyDialog({ open: false, selectedIds: new Set(), sending: false, promos: [], loadingPromos: false });
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Error al enviar');
                  setPromoNotifyDialog(s => ({ ...s, sending: false }));
                }
              }}
              data-testid="promo-notify-send-text"
              title="Envía un solo mensaje de texto con toda la lista, sin fotos (más rápido y ligero)"
            >
              📝 Solo texto
            </Button>
            <Button
              disabled={promoNotifyDialog.selectedIds.size === 0 || promoNotifyDialog.sending}
              onClick={async () => {
                setPromoNotifyDialog(s => ({ ...s, sending: true }));
                try {
                  const { data } = await axios.post(
                    `${API}/promos/notify-sellers`,
                    { seller_ids: Array.from(promoNotifyDialog.selectedIds), text_only: false },
                    { withCredentials: true }
                  );
                  toast.success(`📣 Promos con fotos enviadas a ${data.sent} vendedor(es)`);
                  if (data.errors && data.errors.length > 0) {
                    toast.warning(`Hubo ${data.errors.length} error(es). Revisa logs.`);
                  }
                  setPromoNotifyDialog({ open: false, selectedIds: new Set(), sending: false, promos: [], loadingPromos: false });
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Error al enviar');
                  setPromoNotifyDialog(s => ({ ...s, sending: false }));
                }
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="promo-notify-send"
            >
              {promoNotifyDialog.sending ? 'Enviando…' : `📸 Con fotos (${promoNotifyDialog.selectedIds.size})`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo: Apartado */}
      <Dialog open={apartadoDialog.open} onOpenChange={(o) => !o && !apartadoDialog.submitting && setApartadoDialog(s => ({ ...s, open: false }))}>
        <DialogContent className="max-w-md" data-testid="apartado-dialog">
          <DialogHeader>
            <DialogTitle>📋 Nuevo apartado</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-violet-50 border border-violet-200 rounded p-2 text-xs">
              <p className="font-semibold text-violet-900">Total: ${calculateTotal().toFixed(2)} · {cart.length} ítem(s)</p>
              <p className="text-violet-700">Stock se reserva al crear. El dinero queda pendiente hasta liquidar.</p>
            </div>
            <div>
              <Label>Nombre del cliente *</Label>
              <Input value={apartadoDialog.client_name} onChange={(e) => setApartadoDialog(s => ({ ...s, client_name: e.target.value }))} autoFocus data-testid="apartado-name" />
            </div>
            <div>
              <Label>Teléfono *</Label>
              <Input value={apartadoDialog.client_phone} onChange={(e) => setApartadoDialog(s => ({ ...s, client_phone: e.target.value }))} data-testid="apartado-phone" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Fecha vencimiento *</Label>
                <Input type="date" value={apartadoDialog.due_date} onChange={(e) => setApartadoDialog(s => ({ ...s, due_date: e.target.value }))} data-testid="apartado-due-date" />
              </div>
              <div>
                <Label>Enganche * ($)</Label>
                <Input type="number" step="0.01" value={apartadoDialog.initial_payment} onChange={(e) => setApartadoDialog(s => ({ ...s, initial_payment: e.target.value }))} data-testid="apartado-initial" />
              </div>
            </div>
            <div>
              <Label>Método de pago *</Label>
              <Select value={apartadoDialog.initial_payment_method} onValueChange={(v) => setApartadoDialog(s => ({ ...s, initial_payment_method: v }))}>
                <SelectTrigger data-testid="apartado-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="mixto">Mixto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer p-2 bg-slate-50 rounded border">
              <input type="checkbox" checked={apartadoDialog.deliver_now} onChange={(e) => setApartadoDialog(s => ({ ...s, deliver_now: e.target.checked }))} data-testid="apartado-deliver-now" />
              <span className="text-sm">El cliente se lleva el producto ahora</span>
            </label>
            <div>
              <Label>Notas (opcional)</Label>
              <Input value={apartadoDialog.notes} onChange={(e) => setApartadoDialog(s => ({ ...s, notes: e.target.value }))} data-testid="apartado-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setApartadoDialog(s => ({ ...s, open: false }))} disabled={apartadoDialog.submitting}>Cancelar</Button>
              <Button onClick={submitApartado} disabled={apartadoDialog.submitting} className="bg-violet-600 hover:bg-violet-700" data-testid="apartado-submit">
                {apartadoDialog.submitting ? 'Creando…' : 'Crear apartado'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product create/edit dialog (con aprobación para clerk) */}
      <ProductEditDialog
        open={editDialog.open}
        product={editDialog.product}
        parentProduct={editDialog.parent}
        onClose={() => setEditDialog({ open: false, product: null, parent: null })}
        onSaved={() => { refreshProducts(); }}
      />

      {/* Diálogo de razón al limpiar/cambiar vendedor escaneado */}
      <Dialog open={clearSellerDialog.open} onOpenChange={(o) => !o && setClearSellerDialog({ open: false, action: 'cleared', reason: '', saving: false })}>
        <DialogContent className="max-w-md" data-testid="clear-seller-dialog">
          <DialogHeader>
            <DialogTitle>
              {clearSellerDialog.action === 'cleared'
                ? '¿Por qué limpias al vendedor?'
                : '¿Pasar a Envío y borrar el vendedor escaneado?'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {clearSellerDialog.action === 'cleared'
                ? 'Esta acción avisará al propietario para que sepa por qué su cliente referido no compró.'
                : 'Al cambiar a modo Envío se borrará el vendedor escaneado. El propietario será notificado con tu razón.'}
            </p>
            <div>
              <Label>Razón (obligatoria) *</Label>
              <Input
                value={clearSellerDialog.reason}
                onChange={e => setClearSellerDialog(d => ({ ...d, reason: e.target.value }))}
                placeholder="Ej: cliente cambió de opinión, decidió pedirlo a domicilio, no le alcanzó..."
                data-testid="clear-seller-reason"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setClearSellerDialog({ open: false, action: 'cleared', reason: '', saving: false })}>
                Cancelar
              </Button>
              <Button
                disabled={!clearSellerDialog.reason.trim() || clearSellerDialog.saving}
                onClick={async () => {
                  const r = clearSellerDialog.reason.trim();
                  const action = clearSellerDialog.action;
                  setClearSellerDialog(d => ({ ...d, saving: true }));
                  try {
                    await axios.post(`${API}/sellers/cleared`, {
                      seller_id: selectedSeller,
                      seller_name: scannedSellerName || null,
                      reason: r,
                      action: action
                    }, { withCredentials: true });
                  } catch (err) {
                    // continuar igual; el aviso es best-effort
                  }
                  // Reset vendedor a Local + libera lock
                  const local = (sellers || []).find(s => s.is_local) || (sellers || []).find(s => s.name === 'Local');
                  setSelectedSeller(local ? local.id : '');
                  setSellerLockedByQr(false);
                  setScannedSellerName(null);
                  setClearSellerDialog({ open: false, action: 'cleared', reason: '', saving: false });
                  if (action === 'switched_to_shipment') {
                    _doSwitchToShipmentMode();
                  }
                  toast.success('Aviso enviado al propietario');
                }}
                data-testid="clear-seller-confirm"
                className="bg-rose-600 hover:bg-rose-700"
              >
                {clearSellerDialog.saving ? 'Enviando…' : (clearSellerDialog.action === 'cleared' ? 'Limpiar y avisar' : 'Pasar a envío y avisar')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Botón flotante "Ver carrito" en móvil (visible sólo cuando hay items) */}
      {cart.length > 0 && (
        <button
          type="button"
          onClick={() => document.getElementById('pos-cart-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="lg:hidden fixed bottom-4 right-4 z-40 bg-primary text-white rounded-full shadow-lg px-4 py-3 flex items-center gap-2 text-sm font-semibold active:scale-95 transition"
          data-testid="floating-cart-btn"
        >
          <ShoppingCart size={20} weight="bold" />
          <span>Carrito ({cart.length})</span>
        </button>
      )}

      {/* Diálogo admin: gestionar peticiones pendientes individualmente */}
      <Dialog open={pendingListDialog.open} onOpenChange={(o) => !o && setPendingListDialog({ open: false, loading: false, items: [] })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hourglass size={20} weight="bold" className="text-amber-600" />
              Peticiones pendientes ({pendingListDialog.items.length})
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 space-y-2 pr-1">
            {pendingListDialog.loading ? (
              <p className="text-center text-sm text-muted-foreground py-8">Cargando…</p>
            ) : pendingListDialog.items.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">🎉 No hay peticiones pendientes</p>
            ) : (
              pendingListDialog.items.map((req) => (
                <div key={req.id} className="border border-border rounded p-3 flex items-start justify-between gap-2 hover:bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs px-2 py-0.5 rounded bg-violet-100 text-violet-700 font-mono">{req.type}</span>
                      <span className="text-xs text-muted-foreground">
                        {req.requested_by_user_name || 'Desconocido'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {req.requested_at ? new Date(req.requested_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                      </span>
                    </div>
                    <p className="text-xs text-slate-700 truncate">
                      {req.payload?.reason || req.payload?.folio || JSON.stringify(req.payload).slice(0, 100)}
                    </p>
                    {req.telegram_message_id === null && (
                      <p className="text-[10px] text-rose-600 mt-1">⚠️ Sin mensaje de Telegram entregado</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={async () => {
                      if (!window.confirm(`¿Eliminar esta petición ${req.type}?`)) return;
                      try {
                        await axios.delete(`${API}/approvals/${req.id}`, { withCredentials: true });
                        setPendingListDialog(prev => ({ ...prev, items: prev.items.filter(i => i.id !== req.id) }));
                        toast.success('Petición eliminada');
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Error al eliminar');
                      }
                    }}
                    data-testid={`delete-pending-${req.id}`}
                    title="Eliminar esta petición"
                  >
                    <X size={14} />
                  </Button>
                </div>
              ))
            )}
          </div>
          <div className="pt-2 border-t flex justify-end">
            <Button variant="outline" onClick={() => setPendingListDialog({ open: false, loading: false, items: [] })}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox: vista ampliada de imagen */}
      <Dialog open={!!lightboxImage} onOpenChange={(o) => !o && setLightboxImage(null)}>
        <DialogContent
          className="max-w-4xl p-0 bg-transparent border-0 shadow-none"
          data-testid="image-lightbox"
        >
          {lightboxImage && (
            <div
              className="relative w-full flex flex-col items-center"
              onClick={() => setLightboxImage(null)}
            >
              <img
                src={lightboxImage.url}
                alt={lightboxImage.title || ''}
                className="max-h-[80vh] w-auto object-contain rounded shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
              {lightboxImage.title && (
                <div className="mt-3 px-4 py-1.5 bg-black/70 text-white text-sm rounded-full">
                  {lightboxImage.title}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
